import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { KitId, VoiceType } from '@/hooks/useAudioEngine'
import {
  SCRAMBLER_DEFAULTS,
  applyScramblerParams,
  isPadInScramblerScope,
  type ScramblerParams,
} from '@/lib/scramblerRandomizer'
import { useMidiStore } from '@/stores/midiStore'
import { quantizeRow } from '@/lib/quantize'
import {
  DEFAULT_BANK_IDS,
  MASTER_BUS_ID,
  type BankId,
  type Bank as SharedBank,
  type Bus,
  type BusEffect,
  type BusEffectKind,
  type InputSource,
  type Project as SharedProjectContainer,
  type MidiProjectConfig,
  type SlicerProjectConfig,
} from '@busswerks/shared'

export { MASTER_BUS_ID } from '@busswerks/shared'
export type {
  Bus,
  BusEffect,
  BusEffectKind,
  InputSource,
} from '@busswerks/shared'

// Sixteen pads is a hard product constant: the pad grid is 4×4 and
// every kit fills slots from the top (empty ones are disabled). If a
// future kit grows to 24 slots, pads and types widen together.
export const PADS_PER_PROJECT = 16

/**
 * Valid step counts per project. 16 = one bar, 32 = two bars,
 * 64 = four bars, 128 = eight bars. Each project picks its own — the
 * scheduler reads `project.stepCount` at runtime, so there's no global
 * step constant.
 */
export type StepCount = 16 | 32 | 64 | 128
export const STEP_COUNTS: readonly StepCount[] = [16, 32, 64, 128]

/**
 * How the step-grid visually renders the project. Layout is a UI
 * preference and is orthogonal to stepCount — a 16-step project
 * rendered in "4×4" wastes three quadrants; that's the user's
 * choice. All modes schedule the same underlying StepCell data:
 *
 *   - tracks   2×8 grid, N pads × 16 steps each (parallel editing).
 *              Labelled "2×8" in the switcher. Internal id stays
 *              `tracks` so persisted state from old builds still
 *              resolves.
 *   - grid     8×8 grid, focused pad × 64 steps (only meaningful at
 *              stepCount=64).
 *   - quads    8×8 grid split into 4 quadrants, one pad per quadrant.
 *              Labelled "4×4" in the switcher.
 *   - tracker  vertical: one row per step, one column per pad,
 *              per-step velocity / pitch / FX / slice edited in an
 *              inspector. Modeled on Polyend Tracker's entry style
 *              (no keyboard entry in MVP — inspector panel only).
 *
 * Removed in v9: `bars` — it was effectively `grid` with a different
 * wrap, redundant in practice. Persisted `layoutMode === 'bars'`
 * migrates to `'grid'` in the store's migrate() branch below.
 */
export type LayoutMode = 'tracks' | 'grid' | 'quads' | 'tracker'
export const LAYOUT_MODES: ReadonlyArray<{ id: LayoutMode; label: string }> = [
  { id: 'tracks', label: '2×8' },
  { id: 'grid', label: '8×8' },
  { id: 'quads', label: '4×4' },
  { id: 'tracker', label: 'Tracker' },
]

/**
 * IDs for the master-bus FX bank. The audio engine owns the actual
 * nodes; the store just tracks which are currently engaged so the
 * UI can render them and the engine can ramp AudioParams on change.
 *
 * Per-step FX sends (on StepCell.fx) address the same set — the
 * scheduler automates the master wet gains around each step's
 * trigger time rather than building per-voice wiring.
 */
export type FxId =
  | 'filter'
  | 'delay'
  | 'reverb'
  | 'bitcrush'
  | 'tapeStop'
  | 'stutter'
export const FX_IDS: readonly FxId[] = [
  'filter',
  'delay',
  'reverb',
  'bitcrush',
  'tapeStop',
  'stutter',
]

/**
 * Per-step cell. In Plan 1 this was a plain boolean; Plan 2a widens
 * it for the Tracker layout's per-step richness. Undefined fields
 * inherit from the pad voice + kit defaults. Other layouts render
 * `on` only but the scheduler always reads the full shape.
 *
 * `sliceStart` / `sliceLen` are forward-compat fields for Plan 2b
 * sample chopping — the scheduler ignores them until the voice
 * type `'sample'` ships.
 */
/**
 * ADSR envelope parameters for sample/voice shaping.
 *
 *   a — attack, seconds: 0 → peak (velocity-scaled)
 *   d — decay,  seconds: peak → sustain*peak
 *   s — sustain level, 0..1 (fraction of peak held during the
 *       note-on window)
 *   r — release, seconds: sustain → 0 after note-off
 *
 * Step-level envelopes use `Partial<Envelope>` so a user can override
 * just one parameter (e.g. only `a` for a soft attack) and the rest
 * fall back to the pad-level default. Pad-level envelopes are also
 * `Partial<Envelope>` — missing fields use the engine's hardcoded
 * defaults (snappy attack, no decay, full sustain, short release)
 * so legacy pads without an envelope sound the same as before.
 */
export interface Envelope {
  a: number
  d: number
  s: number
  r: number
}

/**
 * Valid subdivisions for a per-step roll (aka "ratchet" / jungle
 * roll / drum roll). The step's time window is split into this many
 * evenly-spaced hits, each going through the full trigger pipeline
 * (envelope, reverse, time-stretch, FX sends, MIDI-out) with
 * linearly-interpolated pitch and velocity between start and end.
 */
export type RollSubdivisions = 2 | 3 | 4 | 6 | 8 | 12 | 16

export interface StepRoll {
  subdivisions: RollSubdivisions
  /** Semitones applied to hit #1. Defaults to cell.pitch (or 0). */
  pitchStart?: number
  /** Semitones applied to hit #N. Defaults to pitchStart (flat). */
  pitchEnd?: number
  /** 0..1 velocity of hit #1. Defaults to cell.velocity (or 1). */
  velocityStart?: number
  /** 0..1 velocity of hit #N. Defaults to velocityStart (flat). */
  velocityEnd?: number
  /** Per-hit absolute pitch offset in semitones. When present the
   *  scheduler uses `pitchOffsets[i]` instead of the linear
   *  pitchStart → pitchEnd ramp. */
  pitchOffsets?: number[]
  /** Per-hit on/off mask. `false` at index i skips that hit. */
  subdivisionEnabled?: boolean[]
  /** When true, the scheduler sends the roll's last sub-hit through
   *  the master delay so the tail bleeds into the following silent
   *  step. Jungle auto-sets this on rolls it injects; users can set
   *  it on hand-authored rolls too (no dedicated UI yet). */
  tailDelay?: boolean
}

export interface StepCell {
  on: boolean
  /** 0..1. Undefined = pad/kit default velocity. */
  velocity?: number
  /** Semitones (-24..+24). Positive = higher pitch. Applied by the
   *  scheduler to the voice's osc frequency (or sample playbackRate). */
  pitch?: number
  /**
   * Per-step FX sends. Key = FxId, value = 0..1 send amount. Scheduler
   * automates the matching master wet gain around the step's time
   * and lets the tail decay naturally. Only set entries apply.
   */
  fx?: Partial<Record<FxId, number>>
  /** Plan 2b: sample slice start, seconds. Ignored in 2a. */
  sliceStart?: number
  /** Plan 2b: sample slice length, seconds. Ignored in 2a. */
  sliceLen?: number
  /**
   * Gate length in step-grid units (1 = one step = one 16th at 16
   * stepCount). Fractional values are allowed so the inspector can
   * surface dotted (1.5) and triplet (0.6666…) presets alongside
   * integer lengths. The scheduler multiplies `length × stepSec`
   * verbatim, so non-integer values thread through cleanly. Clamped
   * to `stepCount - stepIndex` at render time so a held note never
   * spills past the end of the pattern.
   */
  length?: number
  /**
   * Phase 3: per-step sample manipulation. These layer on top of
   * the pad's default envelope / sample direction at schedule time.
   * Unset → fall back to pad defaults.
   */
  /** Reverse the sample (or slice region) for this hit only. */
  reverse?: boolean
  /** Playback-rate multiplier, 0.5..2. Stacks multiplicatively with
   *  pitch (final `playbackRate = timeStretch × 2^(pitch/12)`). */
  timeStretch?: number
  /** Step-level envelope override. Merged over the pad default. */
  envelope?: Partial<Envelope>
  /** When set, the step emits multiple rapid-fire hits within its
   *  time window with interpolated pitch and velocity. */
  roll?: StepRoll
  /**
   * Fractional offset within the cell's home step, `[0, 1)`. `0` or
   * absent → fires on the grid line. The scheduler adds
   * `microOffset × stepDurationSec` to the fire time so notes can
   * play slightly early/late without moving them to a different
   * step index. Recording captures this from live MIDI input;
   * quantize snaps it back toward 0 or a chosen subdivision.
   */
  microOffset?: number
}

/**
 * Pad index (0-15) → array of `StepCell` of length `stepCount`. Kept
 * as a flat `Record<number, StepCell[]>` so Zustand produces minimal
 * diffs on a single-step toggle.
 */
export type StepGrid = Record<number, StepCell[]>

/** Default cell. Created inline; not a frozen constant because
 *  StepCell is a mutable leaf that we want to spread into a new
 *  object on every update.  */
export const EMPTY_CELL: StepCell = { on: false }

/** True if the cell has any non-default field set beyond `on`.
 *  Used by non-Tracker layouts to render a small indicator in the
 *  corner so users know the cell carries extra data. */
export function isRichStep(cell: StepCell | undefined): boolean {
  if (!cell) return false
  if (cell.velocity !== undefined) return true
  if (cell.pitch !== undefined && cell.pitch !== 0) return true
  if (cell.fx && Object.keys(cell.fx).length > 0) return true
  if (cell.sliceStart !== undefined || cell.sliceLen !== undefined) return true
  if (cell.length !== undefined && cell.length !== 1) return true
  if (cell.reverse) return true
  if (cell.timeStretch !== undefined && cell.timeStretch !== 1) return true
  if (cell.envelope && Object.keys(cell.envelope).length > 0) return true
  if (cell.roll) return true
  if (cell.microOffset !== undefined && cell.microOffset !== 0) return true
  return false
}

/**
 * A user-defined pad that maps to an arbitrary MIDI note/channel.
 * Unlike the 16 built-in kit pads these have no synth voice — they
 * only emit MIDI when triggered, so they're useful for driving
 * external hardware or soft synths. Stored per-project so different
 * projects can target different gear.
 */
export interface MidiPadDef {
  id: string
  label: string
  /** MIDI note number (0..127). */
  note: number
  /** MIDI channel (1..16, matches the display convention). */
  channel: number
  /**
   * Per-pad MIDI output override. When set to a hardware output
   * id, the pad emits MIDI *only* to that output, bypassing the
   * broadcast-via-virtual-input path. When omitted, the pad
   * falls through to the broadcast path (sent to every route
   * whose input is the Sequencer virtual source) — the legacy
   * behaviour.
   */
  outputId?: string
}

/**
 * Plan 2b: user-uploaded sample that replaces the procedural
 * voice for a specific kit pad. `s3Key` is the authoritative
 * resource — the sample audio is fetched at playtime via a
 * signed GET (or public-via-CloudFront once that wire-up
 * lands). `sampleId` is used for cache keying and remote
 * delete/refresh flows.
 *
 * Empty `customSamples` means the project uses the selected
 * kit's procedural voices for every pad — the default.
 */
export interface CustomSampleRef {
  sampleId: string
  s3Key: string
  /** Display name (mirrored from the SAMPLE row so the UI can
   *  render without a separate fetch). Not authoritative — the
   *  server's `SAMPLE#<id>` row is the source of truth. */
  name?: string
}

/**
 * Per-pad MIDI routing target.
 *
 *  - `undefined` (not set in the map): **default for the pad's
 *    kind**. Synth + sample pads default to OFF — they don't
 *    emit MIDI unless explicitly routed. MIDI-kind pads default
 *    to BROADCAST because MIDI is their whole point.
 *    This rule is enforced in `firePad` (SequencerPage), not
 *    the data model, so saved projects without any routing
 *    don't bleed MIDI to every connected device — the common
 *    case where someone is using the sequencer for internal
 *    synth sounds AND has an external MIDI pad routed for a
 *    hardware instrument.
 *  - `'off'`: always suppress MIDI for this pad regardless
 *    of kind.
 *  - `'all'`: broadcast to every route from the Sequencer
 *    virtual source — override the synth/sample default.
 *  - any other string: the pad's MIDI is sent *only* to that
 *    hardware output id.
 */
export type PadMidiRouting = string | 'off' | 'all'

/**
 * Keys Mode — turn the whole 16-pad grid into a chromatic or
 * scale-based MIDI keyboard. When enabled, every pad (kit or
 * override) is ignored at trigger time and replaced with a
 * single pad-layout project: pad 0 plays `rootNote`, pad 1
 * plays `rootNote + scale[1]`, etc. Wraps onto higher octaves
 * once the scale's intervals are exhausted.
 *
 * Useful for live-tapping an external synth from the pad bank
 * without rewriting the project's padDefs.
 */
export type ScaleId =
  | 'chromatic'
  | 'major'
  | 'minor'
  | 'pentatonicMajor'
  | 'pentatonicMinor'
  | 'dorian'
  | 'mixolydian'
  | 'blues'

export const SCALE_INTERVALS: Record<ScaleId, readonly number[]> = {
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  pentatonicMajor: [0, 2, 4, 7, 9],
  pentatonicMinor: [0, 3, 5, 7, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  blues: [0, 3, 5, 6, 7, 10],
}

export const SCALE_LABELS: Record<ScaleId, string> = {
  chromatic: 'Chromatic',
  major: 'Major',
  minor: 'Minor',
  pentatonicMajor: 'Pentatonic major',
  pentatonicMinor: 'Pentatonic minor',
  dorian: 'Dorian',
  mixolydian: 'Mixolydian',
  blues: 'Blues',
}

export interface KeysModeConfig {
  enabled: boolean
  /** Pad 0 plays this note; subsequent pads walk up the scale. */
  rootNote: number
  scale: ScaleId
  /** MIDI channel 1..16. */
  channel: number
  /**
   * Where Keys-mode notes get routed. Follows the same
   * `PadMidiRouting` semantics as per-pad routing:
   *   - undefined → broadcast (all routes from the sequencer)
   *   - 'off'     → no MIDI (useless but allowed)
   *   - 'all'     → explicit broadcast
   *   - string id → target that specific hardware output
   */
  output?: PadMidiRouting
  /**
   * Which pad's voice plays when ANY pad is tapped while Keys mode is
   * on. Captured from `focusedPad` when the mode toggles on and left
   * alone through the session so subsequent pad taps (which would
   * otherwise refocus and swap the source voice — the bug reported
   * 2026-04-22) can't change the instrument out from under the user.
   *
   * Unset on legacy projects that persisted before this field existed;
   * the fire path falls back to `focusedPad` in that case so existing
   * saves keep working. Cleared when Keys mode turns off so the next
   * activation re-captures against whatever pad is in focus then.
   */
  sourcePadIndex?: number
}

const DEFAULT_KEYS_MODE: KeysModeConfig = {
  enabled: false,
  rootNote: 60,
  scale: 'chromatic',
  channel: 1,
}

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
]

export function midiNoteName(midiNote: number): string {
  const n = Math.max(0, Math.min(127, Math.round(midiNote)))
  const octave = Math.floor(n / 12) - 1
  return `${NOTE_NAMES[n % 12]}${octave}`
}

/**
 * Resolve the note for a given pad index under a keys-mode
 * config. Walks the chosen scale's intervals, wrapping onto
 * higher octaves once the scale is exhausted.
 */
export function keysModeNoteFor(
  padIndex: number,
  cfg: Pick<KeysModeConfig, 'rootNote' | 'scale'>,
): number {
  const intervals = SCALE_INTERVALS[cfg.scale]
  const octave = Math.floor(padIndex / intervals.length)
  const step = padIndex % intervals.length
  return Math.max(
    0,
    Math.min(127, cfg.rootNote + octave * 12 + intervals[step]),
  )
}

/**
 * Per-pad content override. Takes precedence over the kit's
 * default voice at the same index. Three variants:
 *
 *   - `synth`  — a procedural voice drawn from the catalog.
 *                Still runs through `trigger(kitId, voice, ...)`
 *                so kit-switching swaps its timbre.
 *   - `sample` — a user-uploaded sample. No synth fallback; if
 *                the AudioBuffer hasn't been decoded yet the
 *                first hit silences while it fetches.
 *   - `midi`   — an external-MIDI-only pad. Emits note+channel
 *                to a routed output (per `padOutputs[padIndex]`)
 *                but produces no audio.
 *
 * `midiNote` is required on every variant so MIDI-in → pad
 * matching and per-pad MIDI-out echoes work uniformly across
 * content kinds.
 */
export type PadContent =
  | {
      kind: 'synth'
      voice: VoiceType
      midiNote: number
      label?: string
      /**
       * Phase 3: pad-level default envelope. Any step on this pad
       * without its own `envelope` override inherits this shape.
       * Missing fields fall back to the engine's defaults
       * (snappy attack, no decay, full sustain, short release).
       */
      envelope?: Partial<Envelope>
    }
  | {
      kind: 'sample'
      sampleId: string
      s3Key: string
      name?: string
      midiNote: number
      /**
       * When set, pad hits play only the named slice region of the
       * sample (resolved at playback time by looking up
       * `sample.slices[].id === sliceId`). Unset or unmatched →
       * play the full sample, preserving pre-slicer behaviour.
       */
      sliceId?: string
      /** Pad-level default envelope — same semantics as synth. */
      envelope?: Partial<Envelope>
      /** Per-pad gain in dBFS. Multiplied with velocity + the
       *  sample / slice gain at playback. Defaults to 0 dB. */
      gainDb?: number
      /**
       * How the pad responds to cell-level `length` locks.
       *
       *   - `'one-shot'` (default): each hit plays the full slice /
       *     sample region to its natural end. Cell `length` is
       *     ignored for audio gating (it still feeds MIDI note-off).
       *     Matches live-path behaviour when cell.length is
       *     undefined — the 99% case for chopped samples where every
       *     slice should ring out.
       *   - `'gated'`: respect cell.length as a hard envelope tail
       *     (release ramps at `length * stepSec`). Used when the
       *     user wants sustained samples (pads, vocal chops) to cut
       *     off at the next step.
       *
       * Absent → `'one-shot'`. Live + offline must agree on this
       * field; mismatched behaviour was the export over-gating bug
       * reported after PR #209.
       */
      playback?: 'one-shot' | 'gated'
    }
  | {
      kind: 'midi'
      label: string
      midiNote: number
      channel: number
    }
  | {
      /** Registry-backed instrument plugin. See
       *  `@busswerks/shared/types/sequencer.ts` for the full doc. */
      kind: 'instrument'
      instrumentId: string
      midiNote: number
      label?: string
      preset?: Record<string, number | string>
      envelope?: Partial<Envelope>
      gainDb?: number
      /** Sampler-instrument sample binding. Only meaningful when
       *  `instrumentId === 'sampler'` (or any future buffer-backed
       *  instrument). Host resolves `sampleId` against the buffer
       *  cache at fire time. */
      sampleId?: string
      s3Key?: string
      sliceId?: string
      sliceStart?: number
      sliceLen?: number
    }
  | {
      kind: 'granular'
      sampleId: string
      s3Key: string
      name?: string
      midiNote: number
      envelope?: Partial<Envelope>
      /** Which preset seeded the current params. UI-only label. */
      preset?: 'freeze' | 'stretch' | 'cloud' | 'reverse-swell'
      grainSizeMs: number
      overlap: number
      detuneCents: number
      playbackRate: number
      reverse: boolean
      positionSec: number
      positionSpread: number
      pitchSpread: number
      loop: boolean
      /** Per-pad gain in dBFS. Multiplied with velocity + the
       *  sample's own gain at playback. Defaults to 0 dB. */
      gainDb?: number
    }

export interface Project {
  id: string
  name: string
  bpm: number
  kitId: KitId
  /** 16 | 32 | 64 | 128 — length of each pad's step array. */
  stepCount: StepCount
  /** Master toggle for MIDI out. Audio playback is independent. */
  midiEnabled: boolean
  /** Master toggle for MIDI in. When false, incoming MIDI from any
   *  hardware input routed to this pattern is ignored — pads don't
   *  trigger, sequencer doesn't take transport messages, FX don't
   *  respond. Defaults true; readers use `?? true` so persisted
   *  patterns predating this field work unchanged. */
  midiInEnabled: boolean
  /** One `StepCell[stepCount]` per pad index (0..PADS_PER_PROJECT-1). */
  steps: StepGrid
  /** User-added MIDI pads — additional pads that fire MIDI-only. */
  midiPads: MidiPadDef[]
  /** One `StepCell[stepCount]` per custom pad, keyed by `MidiPadDef.id`. */
  midiPadSteps: Record<string, StepCell[]>
  /**
   * Plan 2b: sample overrides keyed by pad index. Pads without an
   * entry fall back to the kit's procedural voice. Unset fields on
   * a present entry fall back to defaults too (e.g. `name`
   * optional).
   */
  customSamples?: Record<number, CustomSampleRef>
  /**
   * Per-kit-pad MIDI output override, keyed by pad index. Pads
   * without an entry broadcast to every routed Sequencer output
   * (legacy behaviour). See `PadMidiRouting` for the sentinel
   * values.
   */
  padOutputs?: Record<number, PadMidiRouting>
  /**
   * Per-pad content override, keyed by pad index. When set, the
   * pad plays the override's content regardless of the current
   * kit. When unset, the pad falls through to the kit's default
   * voice at that index (slots 0–7 today; slots 8–15 stay empty
   * until the user assigns something).
   *
   * Supersedes `customSamples` for new writes — the v7 migration
   * promotes any existing `customSamples` entry into a
   * `padDefs[i] = { kind: 'sample', ... }` record.
   */
  padDefs?: Record<number, PadContent>
  /**
   * Keys Mode config (optional). When `enabled` is true, every
   * pad becomes a note in the chosen scale — overrides the
   * pad's normal content at trigger time. Typical use: live
   * performance against an external synth while the sequencer
   * is still driving its own projects.
   */
  keysMode?: KeysModeConfig
  /**
   * Optional row-range loop. When both set and `loopOutStep >=
   * loopInStep`, the scheduler wraps `loopOutStep + 1 → loopInStep`
   * instead of `stepCount → 0`. Odd lengths produce polyrhythms
   * against other patterns / banks whose full lengths don't
   * divide the range. Absent = full-pattern loop.
   */
  loopInStep?: number
  loopOutStep?: number
  /** Pattern swing amount, 0..0.75. Scheduler delays odd 16ths
   *  (steps 1 / 3 / 5 …) by `stepSec × swing × 0.5`. 0 = straight,
   *  0.5 = 16th-triplet feel, 0.66+ = hard shuffle. */
  swing?: number
  /**
   * Saved Scrambler presets for this project. Each preset is
   * a snapshot of the full ScramblerParams (seed + all sliders) plus
   * a user-editable name. Users recall presets via the A/B/C/D
   * buttons in the Scrambler panel to swap between distinct scrambled
   * takes of the same pattern.
   *
   * Persisted on the project (not the store) so reopening a
   * project restores the saved scrambles. The currently-recalled
   * preset id lives on the ephemeral store (`activeScramblerSnapshotId`)
   * because "active preset" is a performance-time concept that
   * shouldn't lock in across sessions.
   */
  scramblerSnapshots?: ScramblerSnapshot[]
  /**
   * Wave 5 scaffolding — piano-roll notes. Each note targets a
   * specific pad at sub-step tick resolution with an independent
   * pitch offset + length. The step grid above stays authoritative
   * for drum-machine-style rhythms; `notes` is the free-form
   * melodic layer. See `@busswerks/shared` for the full shape.
   */
  notes?: import('@busswerks/shared').NoteEvent[]
  /**
   * Wave 5 scaffolding — automation lanes (per-pattern). Each
   * lane maps a `paramPath` (e.g. `pad/0/preset/cutoff`) to a
   * list of tick-keyed breakpoints. The resolver interpolates
   * values at trigger time.
   */
  automation?: import('@busswerks/shared').AutomationLane[]
}

/** One saved snapshot of ScramblerParams for recall. Stored on the
 *  project; named for display. `createdAt` lets us sort presets
 *  chronologically in the UI list. */
export interface ScramblerSnapshot {
  id: string
  name: string
  params: ScramblerParams
  createdAt: string
}

function emptyStepGrid(stepCount: StepCount): StepGrid {
  const grid: StepGrid = {}
  for (let i = 0; i < PADS_PER_PROJECT; i++) {
    grid[i] = Array.from({ length: stepCount }, () => ({ on: false }))
  }
  return grid
}

/**
 * Resize a project's step rows to a new stepCount.
 *  - Growing: pad existing rows with empty cells.
 *  - Shrinking: truncate from the right.
 */
function resizeStepGrid(steps: StepGrid, stepCount: StepCount): StepGrid {
  const next: StepGrid = {}
  for (let i = 0; i < PADS_PER_PROJECT; i++) {
    const row = steps[i] ?? []
    if (row.length === stepCount) {
      next[i] = row
    } else if (row.length > stepCount) {
      next[i] = row.slice(0, stepCount)
    } else {
      next[i] = row.concat(
        Array.from({ length: stepCount - row.length }, () => ({ on: false })),
      )
    }
  }
  return next
}

/**
 * Same size-coercion as `resizeStepGrid` but for the custom-pad step
 * table which is keyed by arbitrary pad ids rather than integer pad
 * indices. Missing rows (newly-added pads) aren't created here — they
 * come from `addMidiPad`; this only resizes existing rows.
 */
function resizeMidiPadSteps(
  steps: Record<string, StepCell[]>,
  stepCount: StepCount,
): Record<string, StepCell[]> {
  const next: Record<string, StepCell[]> = {}
  for (const [id, row] of Object.entries(steps)) {
    if (row.length === stepCount) {
      next[id] = row
    } else if (row.length > stepCount) {
      next[id] = row.slice(0, stepCount)
    } else {
      next[id] = row.concat(
        Array.from({ length: stepCount - row.length }, () => ({ on: false })),
      )
    }
  }
  return next
}

/**
 * Upgrade a legacy `boolean[]` row to a `StepCell[]` row. Used by
 * the v2 → v3 migration and as a safety net for any pre-release
 * persisted state that still has the old shape.
 */
function upgradeRow(row: unknown): StepCell[] {
  if (!Array.isArray(row)) return []
  return row.map(v => {
    if (typeof v === 'boolean') return { on: v }
    if (v && typeof v === 'object' && 'on' in v) {
      return v as StepCell
    }
    return { on: false }
  })
}

/**
 * Construct a fresh project. Seeded with a 4-on-the-floor kick so a
 * brand-new user hears something the moment they press play.
 */
function createProject(
  id: string,
  name: string,
  stepCount: StepCount = 16,
): Project {
  // New patterns start completely empty. Previously pad 1 was
  // pre-seeded with a 4/4 kick so the editor made sound on first
  // play, but that got in the way for anyone not writing drums —
  // turning it off is a one-tap operation and the empty grid is
  // the expected starting state for most workflows.
  const base = emptyStepGrid(stepCount)
  return {
    id,
    name,
    bpm: 120,
    kitId: 'classic',
    stepCount,
    midiEnabled: true,
    midiInEnabled: true,
    steps: base,
    midiPads: [],
    midiPadSteps: {},
  }
}

const DEFAULT_PROJECT_ID = 'default'

function defaultProjects(): Record<string, Project> {
  return {
    [DEFAULT_PROJECT_ID]: createProject(DEFAULT_PROJECT_ID, 'Project 1'),
  }
}

/**
 * True when a project is the pristine seed we create on fresh store
 * initialisation — same id, same default BPM/kit/stepCount, the only
 * active cells are the seed kick's every-4th downbeats, and no custom
 * MIDI pads. Used to keep the seed out of cloud sync (so fresh devices
 * don't each push a "Project 1" and explode the user's library) and
 * to drop it on hydrate when the server already has real projects.
 */
export function isUntouchedDefaultProject(p: Project): boolean {
  if (p.id !== DEFAULT_PROJECT_ID) return false
  if (p.name !== 'Project 1') return false
  if (p.bpm !== 120) return false
  if (p.kitId !== 'classic') return false
  if (p.stepCount !== 16) return false
  if (p.midiPads.length > 0) return false
  if (Object.keys(p.midiPadSteps).length > 0) return false
  // Seed kick: row 0 has a hit on steps 0/4/8/12; all other rows empty.
  for (let padIdx = 0; padIdx < PADS_PER_PROJECT; padIdx++) {
    const row = p.steps[padIdx]
    if (!row) continue
    for (let step = 0; step < row.length; step++) {
      const cell = row[step]
      const expectedOn = padIdx === 0 && step % 4 === 0
      if ((cell?.on ?? false) !== expectedOn) return false
      // Any rich per-step data = user touched it.
      if (cell && isRichStep(cell)) return false
    }
  }
  return true
}

/**
 * Stable serialisation used to detect exact-duplicate projects during
 * `pruneDuplicates`. We hash the user-meaningful fields — not id,
 * createdAt, or anything the store stamps on its own — so two projects
 * with different ids but identical content collapse to one.
 */
function serializeProjectForDedupe(p: Project): string {
  return JSON.stringify({
    name: p.name,
    bpm: p.bpm,
    kitId: p.kitId,
    stepCount: p.stepCount,
    midiEnabled: p.midiEnabled,
    steps: p.steps,
    midiPads: p.midiPads,
    midiPadSteps: p.midiPadSteps,
    padOutputs: p.padOutputs ?? {},
    padDefs: p.padDefs ?? {},
    customSamples: p.customSamples ?? {},
  })
}

// ── Store shape ────────────────────────────────────────────

/**
 * Which step the Tracker layout has focused in the inspector. The
 * grid renders this step's full cell contents (velocity / pitch /
 * FX / slice) in the side panel. Null when nothing is focused.
 *
 * For kit pads, `row = { kind: 'kit', padIndex }`. For custom MIDI
 * pads, `row = { kind: 'midi', padId }`. The inspector renders the
 * same editors regardless of kind.
 */
export type TrackerRow =
  | { kind: 'kit'; padIndex: number }
  | { kind: 'midi'; padId: string }

export interface TrackerFocus {
  row: TrackerRow
  stepIndex: number
}

/** A single entry in Song mode — one project repeated N times. */
export interface SongEntry {
  /** Stable entry id (distinct from projectId so the same project can
   *  appear multiple times in the chain). */
  id: string
  projectId: string
  /** How many times to loop the project before advancing (>= 1). */
  repeats: number
}

/**
 * Song-mode playback cursor. Tracks which song entry is currently
 * playing and how many repeats of that entry are left. Not persisted —
 * always starts fresh when song mode is engaged or a new play begins.
 */
export interface SongCursor {
  entryIndex: number
  repeatsRemaining: number
}

/**
 * ── Container layer (Project → Bank → Pattern) ─────────────────────
 *
 * The existing `projects: Record<id, Project>` below is really a
 * *pattern* collection — flat, 16 pads × N steps per entry — and the
 * code still calls each entry a "Project". Going forward, the
 * user-facing "Project" is a container (from `@busswerks/shared`) that
 * holds four banks (A/B/C/D); each bank references pattern ids from
 * the existing `projects` map plus its own pad layout / kit / etc.
 *
 * This container layer lives ALONGSIDE the flat pattern map so Phase
 * 2 doesn't break any consumer. Consumers that care about the
 * hierarchy (PatternBar, BankSwitcher, /projects list, SequencerPage
 * title) read the container-aware selectors introduced below.
 * Everything else keeps using `selectCurrentProject` and sees the
 * active bank's active pattern exactly as before.
 *
 * Storage shape in the store:
 *   - `projects`          — flat Record<patternId, Pattern-like-Project>
 *   - `containers`        — Record<containerId, ProjectContainer>
 *   - `currentContainerId` — which container is active
 *   - each bank inside a container carries `patternIds: string[]`
 *     pointing into `projects`, plus its own pad layout fields
 */
export interface ProjectContainerBank {
  id: BankId
  label: string
  /** Bank-level pad layout. When a pattern is played from this bank,
   *  its steps run against these pads. Initially seeded from the
   *  owning project's first-pattern fields during v10 migration. */
  kitId: KitId
  padDefs?: Record<number, PadContent>
  padOutputs?: Record<number, PadMidiRouting>
  customSamples?: Record<number, CustomSampleRef>
  /** Per-pad mixer bus routing. Absent / missing key → pad routes
   *  through the master bus. Referenced bus id must exist on the
   *  container's `buses` list; the engine falls back to master when
   *  the reference is stale (e.g. bus deleted without re-routing). */
  padBus?: Record<number, string>
  midiPads: MidiPadDef[]
  midiEnabled: boolean
  keysMode?: KeysModeConfig
  /** Which pattern in this bank is currently editable / played. */
  activePatternId: string
  /** Ordered list of pattern ids that belong to this bank. Entries
   *  resolve against the flat `projects` map. Empty array for unused
   *  banks after migration. */
  patternIds: string[]
}

export interface ProjectContainer {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  /** Server-assigned version for optimistic-concurrency once cloud
   *  sync comes back online in Phase 7. Local-only for now. */
  version: number
  activeBankId: BankId
  banks: Record<BankId, ProjectContainerBank>
  /** Legacy Phase-6 playlist chain. Kept on the container so older
   *  persisted state round-trips, but `scenes` (below) is now the
   *  canonical song model — v11 migration converts these entries
   *  into single-column scenes so old songs keep playing. */
  songMode: boolean
  song: ContainerSongEntry[]
  /** Ableton-session-view song arrangement. Each scene holds one
   *  pattern per bank (A/B/C/D); all four banks play in parallel
   *  while the scene is active. `lengthSteps` is the measure count
   *  in 16th notes — default 16 (one bar). */
  scenes: SongScene[]
  midiConfig?: MidiProjectConfig
  slicerConfig: SlicerProjectConfig
  /** Mixer bus list shared across all banks in this container. Always
   *  starts with the master bus; users can add / rename / remove
   *  additional buses. Absent on pre-v12 persisted containers; v12
   *  migration synthesises `[{ id: MASTER_BUS_ID, … }]`. */
  buses?: Bus[]
  /** Live audio-input routings (hardware channel → bus). Absent on
   *  pre-P5c containers; readers default to empty and the UI
   *  renders an 'Add source' CTA. */
  inputSources?: InputSource[]
  /** When true, every sample-pad hit routes through a Rubber Band
   *  worklet that stretches the sample to match the active
   *  pattern's BPM. Preserves pitch. Absent on legacy projects;
   *  readers default to false. */
  conformToProjectBpm?: boolean
}

/** A single row in the Scenes × Banks grid. */
export interface SongScene {
  id: string
  /** Optional user-given name. Empty string renders as "Scene N". */
  name: string
  /** How long (in 16th-note steps) the scene runs before advancing
   *  to the next scene. Default 16 = one bar. */
  lengthSteps: number
  /** Per-bank pattern id, or null if that bank plays silence in
   *  this scene. Keys are the fixed bank ids (A/B/C/D). */
  cells: Record<BankId, string | null>
}

/** Song entry under the container scheme (replaces the old flat
 *  `SongEntry`). The old `SongEntry` stays in use via `song` below
 *  until every caller migrates to reading the container song. */
export interface ContainerSongEntry {
  id: string
  bankId: BankId
  patternId: string
  repeats: number
}

/**
 * The store's ProjectContainerBank holds `patternIds: string[]` that
 * point into the flat `projects` map, whereas the wire-format
 * `SharedBank` holds `patterns: Record<string, Pattern>` inline. The
 * two shapes are intentionally different (flat references in memory,
 * nested blob over the API) and the sync layer converts between them
 * at Phase 7. We keep imports of the shared types here as a reminder
 * that the semantic meaning is shared even if the runtime shape isn't.
 */
void ({} as SharedBank)
void ({} as SharedProjectContainer)

export interface SequencerState {
  // Project collection. The currently active one drives the scheduler
  // and editor. Additional projects are created via `addProject()`.
  projects: Record<string, Project>
  currentProjectId: string

  /** Container layer — user-facing Projects that wrap banks of
   *  patterns. Phase 2 introduces this alongside the flat `projects`
   *  map; Phase 7's backend migration eventually replaces the
   *  per-device persisted map with server-owned containers. */
  containers: Record<string, ProjectContainer>
  currentContainerId: string

  /**
   * Mapping from local container id → server's authoritative id +
   * last-known version. Persisted so the push loop knows which
   * containers it has already POSTed across SequencerPage mount /
   * unmount cycles; without this the `useRef` syncMap reset every
   * time the user navigated between /projects and /projects/$id,
   * causing the container to get POSTed twice.
   */
  containerSyncState: Record<string, { serverId: string; version: number }>

  /**
   * When the user switches bank / pattern during playback, we don't
   * swap immediately — we queue the switch and apply it on the next
   * downbeat so the groove doesn't stutter mid-pattern. These fields
   * carry the pending targets; `null` means "no switch queued" which
   * is the common case. Transient — not persisted.
   */
  queuedBankId: BankId | null
  queuedPatternId: { bankId: BankId; patternId: string } | null

  /** UI preference for the step-grid rendering. Persisted. */
  layoutMode: LayoutMode

  /** Transient transport state — not persisted. */
  isPlaying: boolean
  /** 0..stepCount-1 during playback, -1 when stopped. Not persisted. */
  currentStep: number

  /**
   * Phase 3c — live note recording mode. Not persisted; always starts
   * at `'off'` after a reload so a user doesn't accidentally overwrite
   * a pattern from a dangling state. Transitions cycle through
   * off → record → overdub → off via `cycleRecordMode()`.
   *
   * `record`   — note-on while playing captures into the pattern,
   *               overwriting whatever was at the target step.
   * `overdub`  — note-on while playing captures into the pattern,
   *               preserving existing rich-cell fields (fx, envelope,
   *               length, roll) and only updating velocity + microOffset.
   */
  recordMode: 'off' | 'record' | 'overdub'

  /** Which pad the non-Tracker layouts focus on. Not persisted. */
  focusedPad: number

  /** Tracker-view inspector focus. Not persisted. */
  trackerFocus: TrackerFocus | null

  /** Momentary FX state from the bank. Not persisted. */
  fxState: Record<FxId, boolean>

  /** Per-FX amount (0..1). Default 1.0 means "full engaged level".
   *  Persisted so the user keeps their mix between sessions. */
  fxLevels: Record<FxId, number>

  /** Performance-FX filter mode. 1 = highpass (drag up for bass cut),
   *  2 = lowpass (drag up for muffle). Default 2 matches the shipped
   *  groovebox-style behaviour so existing projects sound the same
   *  after upgrade. Persisted so the user's choice survives reloads. */
  perfFilterMode: 1 | 2

  /** FX rail latch toggle. When on, perform buttons stick on first
   *  tap instead of releasing; tap again to turn off. Not
   *  persisted — always starts a session in momentary. Mutually
   *  exclusive with `fxHoldMode`. */
  fxLatchMode: boolean
  /** FX rail hold toggle (Koala-style). When on, touching an FX
   *  pad explicitly turns it ON and sets the amount from the
   *  touch position — pointer-up does not disengage and repeated
   *  taps don't toggle off. Mutually exclusive with `fxLatchMode`.
   *  Not persisted. */
  fxHoldMode: boolean

  /** Scrambler (formerly "Jungle Mode") — performance-time
   *  transforms applied on the scheduler's fire path. The underlying
   *  stored pattern is never mutated; toggling
   *  `scramblerParams.enabled` off returns the pattern to its
   *  literal form. Shape defined in `@/lib/scramblerRandomizer`.
   *  Not persisted — always starts a session at defaults so a
   *  re-opened project plays clean. */
  scramblerParams: ScramblerParams
  /** Id of the currently-recalled Scrambler preset. `null`
   *  when the user has tweaked params since the last recall.
   *  Ephemeral — "active preset" is a performance concept that
   *  shouldn't bleed across sessions. */
  activeScramblerSnapshotId: string | null

  /** When true, playback advances through `song` rather than looping
   *  the current project. Persisted. Kept in parallel with
   *  `layoutMode === 'song'` during the Phase 8 song overhaul so
   *  older persisted state still lights up the right UI. */
  songMode: boolean
  /** The chain of projects + repeats played in song mode. Persisted.
   *  Legacy — the canonical arrangement now lives on the container
   *  as `scenes`; this list is only read during v11 migration. */
  song: SongEntry[]
  /** Active song-mode playback cursor. Not persisted. Legacy — the
   *  Scenes × Banks scheduler uses `currentSceneIndex` below. */
  songCursor: SongCursor | null

  /** Which Scene is currently playing. `null` when song-mode
   *  playback hasn't started. Not persisted. */
  currentSceneIndex: number | null
  /** Per-bank "stop" state for song mode. When `true`, that bank's
   *  pattern column is muted until the user taps another cell in
   *  that column (or hits play again). Transient — cleared when
   *  playback stops. */
  bankMuted: Record<BankId, boolean>
  /** Per-pad mute state, scoped per bank. `padMuted[bank][padIndex]`
   *  = true ⇒ scheduler skips firing that pad in that bank. Survives
   *  pattern switches (same bank, different pattern, same mutes)
   *  like a DJ channel mute. Optional inner records — absent banks
   *  have no mutes. */
  padMuted: Record<BankId, Record<number, boolean>>

  // ── mutations ───────────────────────────────────────────

  // Current project editing — coarse
  toggleStep: (row: TrackerRow, stepIndex: number) => void
  clearPad: (row: TrackerRow) => void
  setBpm: (bpm: number) => void
  setKit: (kitId: KitId) => void
  setMidiEnabled: (enabled: boolean) => void
  setMidiInEnabled: (enabled: boolean) => void
  setStepCount: (stepCount: StepCount) => void
  /** Double the current pattern's length by tiling its existing
   *  steps into the new longer row. 16 → 32 copies the pattern
   *  end-to-end; 32 → 64 does the same. No-op at the top of
   *  STEP_COUNTS (128). Useful when the user wants a longer
   *  variation of a loop without re-keying every step. */
  extendCurrentPatternByDuplication: () => void

  // Per-step editing (Tracker inspector)
  setStepVelocity: (
    row: TrackerRow,
    stepIndex: number,
    velocity: number | undefined,
  ) => void
  setStepPitch: (
    row: TrackerRow,
    stepIndex: number,
    pitch: number | undefined,
  ) => void
  setStepFx: (
    row: TrackerRow,
    stepIndex: number,
    fxId: FxId,
    amount: number | undefined,
  ) => void
  setStepLength: (
    row: TrackerRow,
    stepIndex: number,
    length: number | undefined,
  ) => void
  /** Phase 3: toggle reverse playback for this one step. */
  setStepReverse: (
    row: TrackerRow,
    stepIndex: number,
    reverse: boolean | undefined,
  ) => void
  /** Phase 3: time-stretch ratio (0.5..2). Undefined = reset to 1. */
  setStepTimeStretch: (
    row: TrackerRow,
    stepIndex: number,
    stretch: number | undefined,
  ) => void
  /** Phase 3: step-level ADSR override. Passing undefined or an empty
   *  object clears it so the pad default takes over. */
  setStepEnvelope: (
    row: TrackerRow,
    stepIndex: number,
    envelope: Partial<Envelope> | undefined,
  ) => void
  /** Phase 3: roll configuration. Undefined clears the roll. */
  setStepRoll: (
    row: TrackerRow,
    stepIndex: number,
    roll: StepRoll | undefined,
  ) => void
  /** Phase 3a: fractional offset within the home step, `[0, 1)`.
   *  Undefined resets to on-grid. Values outside the range are
   *  clamped at write time. */
  setStepMicroOffset: (
    row: TrackerRow,
    stepIndex: number,
    offset: number | undefined,
  ) => void
  /**
   * Phase 3b: snap microOffsets in the current project (+ optionally
   * MIDI overflow pads) to a chosen subdivision with a 0..1 strength.
   * `scope: 'focused'` only touches the tracker-focused pad's row
   * (falls back to 'allKit' when no focus). `allKit` covers the 16
   * kit pads; `allIncludingMidi` also snaps the custom MIDI pads.
   */
  quantizePattern: (opts: {
    subdivision: 1 | 2 | 3 | 4 | 6 | 8
    strength: number
    scope: 'focused' | 'allKit' | 'allIncludingMidi'
  }) => void
  clearStepExtras: (row: TrackerRow, stepIndex: number) => void

  // Custom MIDI pads
  addMidiPad: (init?: Partial<Omit<MidiPadDef, 'id'>>) => string
  removeMidiPad: (padId: string) => void
  renameMidiPad: (padId: string, label: string) => void
  setMidiPadNote: (padId: string, note: number) => void
  setMidiPadChannel: (padId: string, channel: number) => void
  setMidiPadOutput: (padId: string, outputId: PadMidiRouting | null) => void

  // Custom samples (Plan 2b)
  setCustomSample: (padIndex: number, ref: CustomSampleRef | null) => void
  /**
   * Assign (or clear) per-pad content for any of the 16 pad
   * slots. `null` removes the override and the pad falls back
   * to the kit default.
   */
  setPadContent: (padIndex: number, content: PadContent | null) => void
  /** Bank-targeted variant of `setPadContent`. Writes to the
   *  given bank's pads (and mirrors into every pattern of that
   *  bank), regardless of which bank is currently active. Used
   *  by the slicer's per-slice picker to route a slice to a
   *  specific Bank + Pad without disrupting the user's current
   *  editing context. */
  setPadContentInBank: (
    bankId: BankId,
    padIndex: number,
    content: PadContent | null,
  ) => void

  // Keys Mode
  setKeysMode: (patch: Partial<KeysModeConfig>) => void
  toggleKeysMode: () => void

  // Per-kit-pad MIDI routing
  setPadOutput: (padIndex: number, outputId: PadMidiRouting | null) => void

  // Mixer buses (Phase 2b)
  /** Assign `padIndex` in the current bank to the named bus. Pass
   *  `MASTER_BUS_ID` or missing-bus-id to route through master. */
  setPadBus: (padIndex: number, busId: string) => void
  /** Bank-targeted variant, for flows that set routing without
   *  requiring the bank to be currently active. */
  setPadBusInBank: (bankId: BankId, padIndex: number, busId: string) => void
  /** Add a new user bus to the current container. Returns the new
   *  bus id so callers can immediately route pads to it. */
  addBus: (name?: string) => string
  /** Remove a user bus. No-op for the master bus. Pads still
   *  pointing at it silently fall back to master. */
  removeBus: (busId: string) => void
  renameBus: (busId: string, name: string) => void
  setBusVolume: (busId: string, volume: number) => void
  setBusPan: (busId: string, pan: number) => void
  /** P5b — per-bus output channel pair on the active device. The
   *  bus's stereo output lands on channels `[start, start+1]`;
   *  default 0. Ignored on stereo-only runtimes. */
  setBusOutputChannel: (busId: string, outputChannelStart: number) => void

  // Per-bus FX chain (P8b)
  /** Append a new effect to a bus's chain. Returns the new effect
   *  id so the caller can immediately focus the editor on it. */
  addBusEffect: (
    busId: string,
    kind: BusEffectKind,
    pluginId?: string,
  ) => string
  removeBusEffect: (busId: string, effectId: string) => void
  /** Reorder effects within a bus's chain. Positions not included
   *  in the `ids` array are dropped. Useful for drag-reorder UIs. */
  reorderBusEffects: (busId: string, ids: string[]) => void
  updateBusEffect: (
    busId: string,
    effectId: string,
    patch: Partial<Omit<BusEffect, 'id' | 'kind'>>,
  ) => void

  // Live audio inputs (P5c)
  /** Append a new input source. Returns the id so callers can
   *  immediately edit the fresh row. Defaults channelIndex=0 and
   *  busId=MASTER_BUS_ID when omitted. */
  addInputSource: (init?: Partial<Omit<InputSource, 'id'>>) => string
  removeInputSource: (sourceId: string) => void
  updateInputSource: (
    sourceId: string,
    patch: Partial<Omit<InputSource, 'id'>>,
  ) => void

  // Multi-project
  addProject: (name?: string) => string
  selectProject: (id: string) => void
  renameProject: (id: string, name: string) => void
  deleteProject: (id: string) => void
  resetCurrentProject: () => void
  /**
   * Plan 2b: merge a server project into the store. Used by the
   * sync hook after a remote fetch to overlay projects the user
   * saved on another device, without clobbering the currently
   * selected one.
   */
  hydrateFromRemote: (projects: Project[]) => void
  /**
   * Collapse exact-duplicate-content projects (same name/bpm/kit/
   * steps/midi/etc.) down to a single copy. Returns the IDs that were
   * removed so the caller can also DELETE them server-side — otherwise
   * the next hydrate just pulls them back in. Safe to call any time;
   * returns an empty array when no dupes exist.
   */
  pruneDuplicates: () => string[]

  // UI + transport
  setLayoutMode: (mode: LayoutMode) => void
  setFocusedPad: (padIndex: number) => void
  setTrackerFocus: (focus: TrackerFocus | null) => void
  setPlaying: (playing: boolean) => void
  setCurrentStep: (step: number) => void
  /** Phase 3c — cycle off → record → overdub → off. Wired to the
   *  Transport record button; also MIDI-mappable so a controller's
   *  record key can drive it. */
  cycleRecordMode: () => void
  /** Direct setter — used when external logic needs to force a
   *  specific state (e.g. when playback stops we don't auto-flip
   *  to off in v1, but if we want to later this is the hook). */
  setRecordMode: (mode: 'off' | 'record' | 'overdub') => void
  /**
   * Phase 3c — write a cell into the current project's active row
   * in response to a captured MIDI note. `overdub: false` replaces
   * any existing cell at (row, stepIndex); `overdub: true` preserves
   * rich-cell fields (fx, envelope, length, roll) on an existing
   * on-cell and only updates velocity + microOffset.
   */
  recordCell: (
    row: TrackerRow,
    stepIndex: number,
    opts: {
      microOffset?: number
      velocity?: number
      overdub: boolean
    },
  ) => void
  setFxActive: (fxId: FxId, active: boolean) => void
  setFxLevel: (fxId: FxId, amount: number) => void
  setFxLatchMode: (enabled: boolean) => void
  setFxHoldMode: (enabled: boolean) => void
  setPerfFilterMode: (mode: 1 | 2) => void

  /** Merge partial Scrambler params into the active set. Pass
   *  `{ enabled: false }` to disable in-place without losing the
   *  jitter dialled values. */
  setScramblerParams: (patch: Partial<ScramblerParams>) => void
  /** Bump the scramble seed by 1 so the slice-id permutation
   *  shifts to a new layout. Also forces `enabled = true` so a
   *  "Scramble!" tap from the off state just works. */
  bumpScramblerSeed: () => void
  /** Save the current scramblerParams (seed + all sliders) as a named
   *  preset on the active project. Generated id is returned so the
   *  caller can immediately recall it. No-op when no project is
   *  active. */
  saveScramblerSnapshot: (name?: string) => string | null
  /** Apply a stored preset's params to the live scramblerParams.
   *  Looks up the preset on the active project by id; no-op if
   *  missing. Also sets `activeScramblerSnapshotId` so the UI can
   *  highlight the recalled preset. */
  recallScramblerSnapshot: (id: string) => void
  /** Remove a preset from the active project. Clears
   *  `activeScramblerSnapshotId` if it was the recalled preset. */
  deleteScramblerSnapshot: (id: string) => void
  /** Rename an existing preset without changing its stored params. */
  renameScramblerSnapshot: (id: string, name: string) => void
  /**
   * Bake the current Scrambler output into a NEW pattern in the
   * same bank as the currently-playing pattern. In-scope pads
   * (per `scramblerParams.target`) get their scrambled cells
   * written in verbatim; out-of-scope pads get their source row
   * tiled across the printed length. When `scramblerBars > 1`,
   * the printed pattern is `source.stepCount * scramblerBars`
   * steps long so each bar captures a distinct A / A' / B / A'
   * form-slot variation.
   *
   * Returns the new pattern's id (so callers can announce or
   * navigate) or `null` when no project is active or the
   * Scrambler is disabled.
   */
  printScramblerToPattern: () => string | null

  // Piano roll (Wave 5) ──────────────────────────────────
  /** Append a note to the active project's `notes` array. The
   *  generated id is returned so callers can immediately remove it. */
  addPatternNote: (note: {
    padIndex: number
    pitchOffset: number
    tickStart: number
    tickLength: number
    velocity: number
  }) => string | null
  /** Remove a piano-roll note by id. No-op if missing. */
  removePatternNote: (noteId: string) => void
  /** Remove every piano-roll note from the active pattern. */
  clearPatternNotes: () => void
  /** Patch a note in place. Any subset of `{ padIndex, tickStart,
   *  tickLength, pitchOffset, velocity }` can be passed; fields not
   *  in the patch stay unchanged. No-op if the note doesn't exist. */
  updatePatternNote: (
    noteId: string,
    patch: Partial<{
      padIndex: number
      tickStart: number
      tickLength: number
      pitchOffset: number
      velocity: number
    }>,
  ) => void

  // Automation (Wave 5) ─────────────────────────────────
  /** Create a new automation lane with the given param path. The
   *  generated id is returned so callers can immediately target
   *  the lane for breakpoint edits. */
  addAutomationLane: (paramPath: string) => string | null
  /** Remove an automation lane by id. No-op if missing. */
  removeAutomationLane: (laneId: string) => void
  /** Remove every automation lane from the active pattern. */
  clearAutomationLanes: () => void
  /** Duplicate an existing lane, copying paramPath + all points +
   *  min/max into a new lane with a fresh id. Returns the new id
   *  for callers that want to immediately focus it. */
  duplicateAutomationLane: (laneId: string) => string | null
  /** Toggle a lane's enabled flag. */
  setAutomationLaneEnabled: (laneId: string, enabled: boolean) => void
  /** Set the lane's canvas Y-axis range. Either bound can be
   *  `undefined` to clear (falls back to 0 min / inferred max). */
  setAutomationLaneRange: (
    laneId: string,
    min: number | undefined,
    max: number | undefined,
  ) => void
  /** Append a breakpoint to a lane. Callers pass raw values —
   *  the store keeps points sorted by tick on every write. */
  addAutomationPoint: (
    laneId: string,
    point: { tick: number; value: number },
  ) => void
  /** Remove the nearest breakpoint to a tick (within a tolerance)
   *  from a lane. Used by the UI to delete by clicking on a
   *  rendered dot. */
  removeAutomationPointAtTick: (
    laneId: string,
    tick: number,
    toleranceTicks?: number,
  ) => void
  /** Move a specific breakpoint to a new `{ tick, value }`. The
   *  store identifies the target by its original tick so callers
   *  can drag without hand-managing point ids. Out-of-range
   *  moves are clamped before write. */
  moveAutomationPoint: (
    laneId: string,
    fromTick: number,
    to: { tick: number; value: number },
  ) => void

  /** Toggle realtime BPM conform (Phase 5) on the active
   *  project. When on, every sample-pad hit with a known source
   *  BPM routes through a Rubber Band AudioWorklet so pitch is
   *  preserved while tempo tracks the pattern. */
  setConformToProjectBpm: (enabled: boolean) => void

  // Song mode
  setSongMode: (enabled: boolean) => void
  addSongEntry: (projectId: string, repeats?: number) => void
  removeSongEntry: (entryId: string) => void
  setSongRepeats: (entryId: string, repeats: number) => void
  moveSongEntry: (entryId: string, direction: -1 | 1) => void
  setSongCursor: (cursor: SongCursor | null) => void

  // Song (Scenes × Banks)
  /** Append a new scene with 16-step default length + empty cells. */
  addScene: () => void
  removeScene: (sceneId: string) => void
  renameScene: (sceneId: string, name: string) => void
  setSceneLengthSteps: (sceneId: string, lengthSteps: number) => void
  moveScene: (sceneId: string, direction: -1 | 1) => void
  /** Assign a pattern to a bank column of a scene (or `null` to
   *  clear). `bankId` must be one of the fixed A/B/C/D. */
  setSceneCell: (
    sceneId: string,
    bankId: BankId,
    patternId: string | null,
  ) => void
  /** Jump the song playhead to a specific scene. When not playing,
   *  stages the scene for when play begins. */
  selectScene: (sceneIndex: number | null) => void
  /** Mute one column in Song mode (Ableton's column Stop button).
   *  Tapping a cell in that column unmutes the bank and sets the
   *  pattern simultaneously. */
  setBankMuted: (bankId: BankId, muted: boolean) => void
  /** Toggle (or set) a per-pad mute inside the given bank. `muted =
   *  undefined` toggles the current state. Scheduler reads this on
   *  the fire path and silently skips the pad when muted. */
  setPadMuted: (bankId: BankId, padIndex: number, muted?: boolean) => void

  // ── Queued switches (apply on the next downbeat) ─────────
  /** Queue a bank switch to apply at the next measure wrap. If no
   *  playback is running, selectBank applies immediately via its
   *  usual path; this action is only meaningful mid-play. */
  queueBankSwitch: (bankId: BankId) => void
  /** Queue a pattern switch in a specific bank. When the wrap hits,
   *  the scheduler calls applyQueuedSwitches which calls the
   *  non-queued selectBank/setActivePatternInBank. */
  queuePatternSwitch: (bankId: BankId, patternId: string) => void
  /** Clear any pending queued switches — used when stopping playback
   *  or when the user changes their mind before the downbeat. */
  cancelQueuedSwitches: () => void
  /** Flush queued bank + pattern to the active state. Fired by the
   *  scheduler at the wrap boundary. No-op when nothing is queued. */
  applyQueuedSwitches: () => void

  // ── Container sync state (Phase A) ────────────────────────
  /** Record that a local container was POSTed to the server and
   *  received the given uuid/version. Called from the sync hook on
   *  successful POST or PUT. */
  recordContainerSync: (
    localId: string,
    serverId: string,
    version: number,
  ) => void
  /** Drop the sync record (e.g. after local delete). */
  forgetContainerSync: (localId: string) => void
  /** Replace one local container id with another (after POST remap
   *  where localId !== serverId). Keeps `currentContainerId`,
   *  `containerSyncState`, and the `containers` map in lock-step. */
  remapContainerId: (oldId: string, newId: string, version: number) => void

  // ── Container / Bank actions (Phase 2) ──────────────────────
  /** Create a new top-level Project container with four empty banks. */
  addContainer: (name?: string) => string
  /** Insert a container minted server-side (POST response) into the
   *  local store WITHOUT letting the push loop re-POST it. The sync
   *  record is seeded atomically so the debounced push treats it as
   *  already-synced. Used by the `+ New Project` dialog so the editor
   *  opens on the server id with no "Loading project…" gap. */
  insertServerContainer: (args: {
    id: string
    name: string
    version: number
  }) => void
  /** Switch to another container. Its active bank + pattern become the
   *  editor's current view. */
  selectContainer: (containerId: string) => void
  renameContainer: (containerId: string, name: string) => void
  /** Promote the local `default-project` scratch pad into a proper
   *  UUID-keyed container with a user-supplied name. The sync push
   *  loop explicitly skips `DEFAULT_CONTAINER_ID`, so without this
   *  promotion the user's work on the default project is local-only
   *  and will be lost on any state-reset. After the promotion, the
   *  scratch pad is re-seeded as a fresh pristine default so the
   *  user has a clean slate for their next session.
   *
   *  Returns the new UUID-keyed container id, or null if the current
   *  container isn't the default project. */
  saveDefaultProject: (name: string) => string | null
  /** Idempotently create the anonymous local-jam container + seed
   *  a first pattern. Returns the container id so callers can
   *  select it. Safe to call from an unauthenticated route — the
   *  container stays local-only per the sync loop's skip rule. */
  ensureLocalJam: () => string
  deleteContainer: (containerId: string) => void
  /** Switch the active bank inside the current container. The bank's
   *  remembered `activePatternId` becomes the editor's current pattern;
   *  if the bank has no patterns yet, one is auto-created. */
  selectBank: (bankId: BankId) => void
  renameBank: (bankId: BankId, label: string) => void
  setBankKit: (bankId: BankId, kitId: KitId) => void
  /** Set the currently-edited pattern inside a specific bank. Routing
   *  through the bank (rather than flat `selectProject`) so
   *  bank-specific pattern ownership stays consistent. */
  setActivePatternInBank: (bankId: BankId, patternId: string) => void
  /** Add a new pattern to the given bank (or the active bank).
   *  Creates a fresh Pattern in `projects`, appends its id to the
   *  bank's `patternIds`, and sets it active. */
  addPatternToBank: (bankId?: BankId, name?: string) => string
  /** Set the active pattern's row-range loop window. Pass `null`
   *  for either endpoint to clear that side (or both to disable
   *  the range and loop the full pattern). Silently clamps to
   *  valid step indices and enforces `in <= out`. */
  setPatternLoopRange: (inStep: number | null, outStep: number | null) => void
  /** Set the active pattern's swing amount. Clamps to 0..0.75. */
  setPatternSwing: (amount: number) => void
  /** Deep-clone an existing pattern into the same (or target) bank.
   *  Default name = `${source} copy`; default target = source's bank.
   *  Returns the new pattern id, or an empty string on failure
   *  (missing source). Clones `steps` + `midiPadSteps` cell-by-cell
   *  so future edits to either side don't leak. */
  duplicatePattern: (
    sourcePatternId: string,
    targetBankId?: BankId,
    newName?: string,
  ) => string
  /** Remove a pattern from a bank. Deletes the underlying flat
   *  `projects` entry too (patterns are bank-internal). */
  removePatternFromBank: (bankId: BankId, patternId: string) => void
  /** Project-level MIDI override — lifts the current global midiStore
   *  values into `container.midiConfig` so the user starts editing
   *  from the same routing they were seeing. Phase 6 wires this up. */
  initProjectMidiOverride: () => void
  /** Drop the project's MIDI override — routing falls back to global. */
  clearProjectMidiOverride: () => void
  setProjectMidiRoutes: (
    routes: Array<{ inputId: string; outputId: string }>,
  ) => void
  setProjectOutputLatency: (outputId: string, ms: number) => void
  /** Add a sample to the current project's slicer filter list. */
  addProjectSample: (sampleId: string) => void
  removeProjectSample: (sampleId: string) => void
  setActiveProjectSample: (sampleId: string | undefined) => void
}

export const selectCurrentProject = (s: SequencerState): Project =>
  s.projects[s.currentProjectId] ?? Object.values(s.projects)[0]

function updateCurrent(
  state: SequencerState,
  patch: (p: Project) => Project,
): Partial<SequencerState> {
  const curr = state.projects[state.currentProjectId]
  if (!curr) return state
  return {
    projects: { ...state.projects, [state.currentProjectId]: patch(curr) },
  }
}

/** Container-level analogue of `updateCurrent`. Stamps `updatedAt`
 *  so the sync loop notices the change. */
function patchCurrentContainer(
  state: SequencerState,
  patch: (c: ProjectContainer) => ProjectContainer,
): Partial<SequencerState> {
  const curr = state.containers[state.currentContainerId]
  if (!curr) return state
  const next = patch(curr)
  return {
    containers: {
      ...state.containers,
      [state.currentContainerId]: {
        ...next,
        updatedAt: new Date().toISOString(),
      },
    },
  }
}

/**
 * Update one cell in either a kit-pad row (`steps[padIndex]`) or a
 * custom-pad row (`midiPadSteps[padId]`) using a single shared patch
 * function. Keeps the per-step editors (velocity, pitch, fx, length)
 * symmetric across both kinds without duplicating the action bodies.
 */
function updateCell(
  project: Project,
  row: TrackerRow,
  stepIndex: number,
  patch: (cell: StepCell) => StepCell,
): Project {
  if (row.kind === 'kit') {
    const stepRow = project.steps[row.padIndex] ?? []
    if (stepIndex < 0 || stepIndex >= stepRow.length) return project
    const next = [...stepRow]
    next[stepIndex] = patch(next[stepIndex] ?? { on: false })
    return {
      ...project,
      steps: { ...project.steps, [row.padIndex]: next },
    }
  }
  const stepRow = project.midiPadSteps[row.padId] ?? []
  if (stepIndex < 0 || stepIndex >= stepRow.length) return project
  const next = [...stepRow]
  next[stepIndex] = patch(next[stepIndex] ?? { on: false })
  return {
    ...project,
    midiPadSteps: { ...project.midiPadSteps, [row.padId]: next },
  }
}

/** Clear a whole row (kit pad or custom MIDI pad). */
function clearRow(project: Project, row: TrackerRow): Project {
  const empty = Array.from({ length: project.stepCount }, () => ({
    on: false,
  }))
  if (row.kind === 'kit') {
    return {
      ...project,
      steps: { ...project.steps, [row.padIndex]: empty },
    }
  }
  return {
    ...project,
    midiPadSteps: { ...project.midiPadSteps, [row.padId]: empty },
  }
}

function emptyFxState(): Record<FxId, boolean> {
  const out = {} as Record<FxId, boolean>
  for (const id of FX_IDS) out[id] = false
  return out
}

/** All FX default to full amount (1.0) — engaging an effect behaves
 *  like the old binary bank until the user moves its slider. */
function defaultFxLevels(): Record<FxId, number> {
  const out = {} as Record<FxId, number>
  for (const id of FX_IDS) out[id] = 1
  return out
}

function genId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function clamp127(n: number): number {
  return Math.max(0, Math.min(127, Math.round(n)))
}

function clampChannel(n: number): number {
  return Math.max(1, Math.min(16, Math.round(n)))
}

// ── Container helpers ──────────────────────────────────────

export const DEFAULT_CONTAINER_ID = 'default-project'

/**
 * Public anonymous jam container id. The landing page exposes a
 * "Try the sequencer" CTA that lands on `/werkshop/projects/local-jam`
 * without requiring auth — we keep the container local-only so
 * unauthenticated visitors can iterate without hitting the server.
 * Treated the same as `default-project` by the cloud sync loop (push
 * skip list) so signed-in users who also visit the jam URL don't
 * accidentally upload their anonymous doodle to their account. */
export const LOCAL_JAM_CONTAINER_ID = 'local-jam'

/**
 * True when the given container looks like the v10 migration's seed
 * (every-4-step kick on bank A pad 0, default kit, default bpm,
 * nothing else). Used by the sync hook to drop the local seed when
 * remote already has projects — without this, every fresh device
 * would POST its seed as a new project, producing the "Untitled
 * project" duplicate that kept reappearing on every session.
 */
export function isPristineDefaultContainer(
  container: ProjectContainer,
  patterns: Record<string, Project>,
): boolean {
  if (container.id !== DEFAULT_CONTAINER_ID) return false
  if (container.name !== 'Untitled project') return false
  if (container.activeBankId !== 'A') return false
  if (container.song.length > 0) return false
  if (container.midiConfig) return false
  if (container.slicerConfig.sampleIds.length > 0) return false
  for (const bankId of DEFAULT_BANK_IDS) {
    const bank = container.banks[bankId]
    if (!bank) continue
    if (bankId === 'A') {
      if (bank.kitId !== 'classic') return false
      if (bank.padDefs && Object.keys(bank.padDefs).length > 0) return false
      if (bank.padOutputs && Object.keys(bank.padOutputs).length > 0)
        return false
      if (bank.customSamples && Object.keys(bank.customSamples).length > 0)
        return false
      if (bank.midiPads.length > 0) return false
      if (bank.keysMode?.enabled) return false
      if (bank.patternIds.length !== 1) return false
      const pattern = patterns[bank.patternIds[0]]
      if (!pattern) return false
      if (pattern.bpm !== 120) return false
      if (pattern.stepCount !== 16) return false
      for (let padIdx = 0; padIdx < PADS_PER_PROJECT; padIdx++) {
        const row = pattern.steps[padIdx]
        if (!row) continue
        for (let step = 0; step < row.length; step++) {
          const expected = padIdx === 0 && step % 4 === 0
          const actual = row[step]?.on ?? false
          if (actual !== expected) return false
          if (row[step] && isRichStep(row[step])) return false
        }
      }
    } else {
      // Banks B/C/D should be empty on a pristine seed.
      if (bank.patternIds.length > 0) return false
      if (bank.padDefs && Object.keys(bank.padDefs).length > 0) return false
      if (bank.midiPads.length > 0) return false
    }
  }
  return true
}

/** Build an empty bank for a freshly-minted container. Pad layout /
 *  kit fields are seeded from the fallback arg so v10 migration can
 *  lift the pre-refactor pattern's fields into bank A while leaving
 *  banks B/C/D untouched. */
function createBank(
  id: BankId,
  opts?: Partial<Omit<ProjectContainerBank, 'id' | 'label'>>,
): ProjectContainerBank {
  return {
    id,
    label: `Bank ${id}`,
    kitId: opts?.kitId ?? 'classic',
    padDefs: opts?.padDefs,
    padOutputs: opts?.padOutputs,
    customSamples: opts?.customSamples,
    midiPads: opts?.midiPads ?? [],
    midiEnabled: opts?.midiEnabled ?? true,
    keysMode: opts?.keysMode,
    activePatternId: opts?.activePatternId ?? '',
    patternIds: opts?.patternIds ?? [],
  }
}

function createEmptyContainer(
  id: string,
  name: string,
  firstPatternId?: string,
  bankASeed?: Partial<Omit<ProjectContainerBank, 'id' | 'label'>>,
): ProjectContainer {
  const now = new Date().toISOString()
  const aSeed = bankASeed ?? {}
  const banks: Record<BankId, ProjectContainerBank> = {
    A: createBank('A', {
      ...aSeed,
      activePatternId: firstPatternId ?? aSeed.activePatternId ?? '',
      patternIds: firstPatternId
        ? [
            firstPatternId,
            ...(aSeed.patternIds ?? []).filter(p => p !== firstPatternId),
          ]
        : (aSeed.patternIds ?? []),
    }),
    B: createBank('B'),
    C: createBank('C'),
    D: createBank('D'),
  }
  return {
    id,
    name,
    createdAt: now,
    updatedAt: now,
    version: 0,
    activeBankId: 'A',
    banks,
    songMode: false,
    song: [],
    scenes: [],
    slicerConfig: { sampleIds: [] },
    // Phase 2b: every new container starts with a master bus so
    // the engine always has a valid destination for pads that
    // don't opt into a user bus. Since the Plugin tab's default view
    // is the master FFT Analyzer (see PR #224), we seed the analyzer
    // here too so it's available the moment a fresh project opens,
    // without depending on a post-mount `useEffect` to add it later.
    // The sentinel effect id (`MASTER_FFT_ANALYZER_EFFECT_ID`) makes
    // the migration below idempotent across re-hydrations.
    buses: [
      {
        id: MASTER_BUS_ID,
        name: 'Master',
        volume: 1,
        pan: 0,
        effects: [
          {
            id: MASTER_FFT_ANALYZER_EFFECT_ID,
            kind: 'plugin',
            pluginId: 'fft-analyzer',
            enabled: true,
          },
        ],
      },
    ],
  }
}

/** Stable effect id for the default Master FFT Analyzer. Stamped on
 *  every new container + every v13 migration so (a) the read path
 *  always finds one row regardless of how the project was born, and
 *  (b) re-running the migration doesn't stack duplicates. */
export const MASTER_FFT_ANALYZER_EFFECT_ID = 'fft-master-default'

/**
 * Idempotent self-heal: make sure the given container has a master
 * bus whose `effects` list contains the FFT analyzer. Returns a new
 * container if any change was needed, or the same reference when
 * nothing changed (so caller-side identity checks keep working).
 *
 * Runs on every rehydrate via `onRehydrateStorage` below — covers the
 * case where a user's state is already at `version: 13` (so zustand-
 * persist skips `migrate`) but the master FFT was lost to a prior bug
 * or a manual edit. The v13 migration only runs when `fromVersion <
 * targetVersion`; this helper runs every time and converges the state
 * to "master exists + carries the FFT analyzer" idempotently.
 */
export function ensureMasterFftEffect(
  container: ProjectContainer,
): ProjectContainer {
  const busesIn = container.buses ?? []
  const masterExisting = busesIn.find(b => b.id === MASTER_BUS_ID)
  const masterEffects = masterExisting?.effects ?? []
  const hasAnalyzer = masterEffects.some(e => e.pluginId === 'fft-analyzer')
  if (masterExisting && hasAnalyzer) return container

  const seededMaster: Bus = {
    id: MASTER_BUS_ID,
    name: masterExisting?.name ?? 'Master',
    volume: masterExisting?.volume ?? 1,
    pan: masterExisting?.pan ?? 0,
    effects: hasAnalyzer
      ? masterEffects
      : [
          ...masterEffects,
          {
            id: MASTER_FFT_ANALYZER_EFFECT_ID,
            kind: 'plugin',
            pluginId: 'fft-analyzer',
            enabled: true,
          },
        ],
  }

  const nextBuses = masterExisting
    ? busesIn.map(b => (b.id === MASTER_BUS_ID ? seededMaster : b))
    : [seededMaster, ...busesIn]

  return { ...container, buses: nextBuses }
}

function defaultContainers(
  firstPatternId: string,
): Record<string, ProjectContainer> {
  return {
    [DEFAULT_CONTAINER_ID]: createEmptyContainer(
      DEFAULT_CONTAINER_ID,
      'Untitled project',
      firstPatternId,
    ),
  }
}

// ── Container selectors (exported for consumers) ─────────────

export const selectCurrentContainer = (
  s: SequencerState,
): ProjectContainer | null =>
  s.containers[s.currentContainerId] ?? Object.values(s.containers)[0] ?? null

/**
 * Effective scene-loop length in 16th-note steps. The scheduler
 * wraps at this value when song mode is active; the UI's progress
 * bar uses it too so the indicator and the audio line up.
 *
 * Guards against two cases where `scene.lengthSteps` on its own
 * would mis-cycle:
 *   1. A scene saved with `lengthSteps` shorter than the longest
 *      assigned pattern — the scheduler would wrap at the shorter
 *      value, cutting the pattern off mid-cycle. Bumps up to the
 *      longest pattern's stepCount in that case.
 *   2. Empty scenes (no patterns assigned) — falls back to the
 *      raw `scene.lengthSteps` or 16 so the clock still ticks.
 */
export function computeSceneLength(
  scene: SongScene,
  projects: Record<string, { stepCount?: number }>,
): number {
  const longest = Math.max(
    0,
    ...(['A', 'B', 'C', 'D'] as BankId[]).map(b => {
      const pid = scene.cells[b]
      const p = pid ? projects[pid] : null
      return p?.stepCount ?? 0
    }),
  )
  if (longest === 0) return Math.max(1, scene.lengthSteps || 16)
  return Math.max(scene.lengthSteps, longest)
}

export const selectCurrentBank = (
  s: SequencerState,
): ProjectContainerBank | null => {
  const c = selectCurrentContainer(s)
  if (!c) return null
  return c.banks[c.activeBankId] ?? null
}

/** Stable fallback bus list for containers that predate v12 /
 *  somehow skipped the migration. A lone master entry is the
 *  minimum viable state: the runtime always routes unknown pads
 *  through master, so components can render it as a real strip
 *  without the user having to first click "Add bus". Frozen so the
 *  reference stays stable across selector calls (prevents redundant
 *  re-renders in Zustand consumers). */
const FALLBACK_BUSES: readonly Bus[] = Object.freeze([
  Object.freeze({ id: MASTER_BUS_ID, name: 'Master', volume: 1, pan: 0 }),
])

/**
 * Current container's mixer buses, always with at least the master
 * entry. If the container is missing `buses` (pre-v12 legacy state,
 * or a synthetic container built before addProject seeded the
 * default bus list) we return a frozen single-entry array so the
 * BusRack UI always shows a master strip and dependent features
 * (input routing, per-pad bus assignment) always have a valid
 * destination to bind against.
 */
export const selectCurrentBuses = (s: SequencerState): readonly Bus[] => {
  const c = selectCurrentContainer(s)
  const stored = c?.buses as readonly Bus[] | undefined
  if (!stored || stored.length === 0) return FALLBACK_BUSES
  // Guarantee a master entry even if the container somehow lost it.
  // We don't write the fallback back into the store (selectors are
  // read-only), so adding / renaming pushes through the real action.
  if (stored.some(b => b.id === MASTER_BUS_ID)) return stored
  return [FALLBACK_BUSES[0]!, ...stored]
}

/** Active pattern id: prefer the active bank's remembered
 *  `activePatternId`; fall back to the flat `currentProjectId` so
 *  components that haven't migrated to the container layer yet keep
 *  pointing at whatever pattern was selected before. */
export const selectCurrentPatternId = (s: SequencerState): string | null => {
  const bank = selectCurrentBank(s)
  if (bank && bank.activePatternId) return bank.activePatternId
  return s.currentProjectId || null
}

export const useSequencerStore = create<SequencerState>()(
  persist(
    (set, get) => ({
      projects: defaultProjects(),
      currentProjectId: DEFAULT_PROJECT_ID,
      containers: defaultContainers(DEFAULT_PROJECT_ID),
      currentContainerId: DEFAULT_CONTAINER_ID,
      containerSyncState: {},
      queuedBankId: null,
      queuedPatternId: null,
      layoutMode: 'grid',
      isPlaying: false,
      currentStep: -1,
      recordMode: 'off',
      focusedPad: 0,
      trackerFocus: null,
      fxState: emptyFxState(),
      fxLevels: defaultFxLevels(),
      perfFilterMode: 2,
      fxLatchMode: false,
      fxHoldMode: false,
      scramblerParams: SCRAMBLER_DEFAULTS,
      activeScramblerSnapshotId: null,
      songMode: false,
      song: [],
      songCursor: null,
      currentSceneIndex: null,
      bankMuted: { A: false, B: false, C: false, D: false },
      padMuted: { A: {}, B: {}, C: {}, D: {} },

      toggleStep: (row, stepIndex) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => ({ ...c, on: !c.on })),
          ),
        ),

      clearPad: row =>
        set(state => updateCurrent(state, p => clearRow(p, row))),

      setBpm: bpm =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            bpm: Math.max(40, Math.min(240, Math.round(bpm))),
          })),
        ),

      setKit: kitId =>
        set(state => updateCurrent(state, p => ({ ...p, kitId }))),

      setMidiEnabled: midiEnabled =>
        set(state => updateCurrent(state, p => ({ ...p, midiEnabled }))),

      setMidiInEnabled: midiInEnabled =>
        set(state => updateCurrent(state, p => ({ ...p, midiInEnabled }))),

      setStepCount: stepCount =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            stepCount,
            steps: resizeStepGrid(p.steps, stepCount),
            midiPadSteps: resizeMidiPadSteps(p.midiPadSteps, stepCount),
          })),
        ),

      extendCurrentPatternByDuplication: () =>
        set(state =>
          updateCurrent(state, p => {
            // Next supported length that's > current stepCount. When
            // the user is already at the top of STEP_COUNTS (128)
            // there's nothing to extend into — bail.
            const next = STEP_COUNTS.find(c => c > p.stepCount) as
              | StepCount
              | undefined
            if (!next) return p
            const factor = Math.max(1, Math.round(next / p.stepCount))
            // Each pad's step row gets its existing cells repeated
            // end-to-end until we hit the new length. Same for
            // midi-pad rows, so a 16-step groove with both kit and
            // midi activity extends cleanly instead of leaving the
            // midi rows empty in the tail half.
            const tileRow = (row: StepCell[] | undefined): StepCell[] => {
              const src = row ?? []
              if (src.length === 0) {
                return Array.from({ length: next }, () => ({ on: false }))
              }
              const out: StepCell[] = []
              for (let i = 0; i < factor; i++) {
                for (const cell of src) out.push({ ...cell })
              }
              // Truncate to exactly `next` in case factor × src wasn't
              // a clean multiple (shouldn't happen with power-of-two
              // STEP_COUNTS, but cheap insurance).
              return out.slice(0, next)
            }
            const nextSteps: StepGrid = {}
            for (const [padIdx, row] of Object.entries(p.steps)) {
              nextSteps[Number(padIdx)] = tileRow(row)
            }
            const nextMidiPadSteps: Record<string, StepCell[]> = {}
            for (const [padId, row] of Object.entries(p.midiPadSteps)) {
              nextMidiPadSteps[padId] = tileRow(row)
            }
            return {
              ...p,
              stepCount: next,
              steps: nextSteps,
              midiPadSteps: nextMidiPadSteps,
            }
          }),
        ),

      // ── Per-step editing ──
      setStepVelocity: (row, stepIndex, velocity) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (velocity === undefined) delete next.velocity
              else next.velocity = Math.max(0, Math.min(1, velocity))
              return next
            }),
          ),
        ),

      setStepPitch: (row, stepIndex, pitch) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (pitch === undefined || pitch === 0) delete next.pitch
              else next.pitch = Math.max(-24, Math.min(24, pitch))
              return next
            }),
          ),
        ),

      setStepFx: (row, stepIndex, fxId, amount) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const nextFx = { ...(c.fx ?? {}) }
              if (amount === undefined || amount <= 0) {
                delete nextFx[fxId]
              } else {
                nextFx[fxId] = Math.max(0, Math.min(1, amount))
              }
              const next: StepCell = { ...c }
              if (Object.keys(nextFx).length === 0) {
                delete next.fx
              } else {
                next.fx = nextFx
              }
              return next
            }),
          ),
        ),

      setStepLength: (row, stepIndex, length) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (length === undefined || length === 1) {
                delete next.length
              } else {
                // Clamp: never hold past the end of the project.
                // Fractional values (dotted 1.5, triplet 2/3) are
                // preserved — the scheduler multiplies verbatim.
                const max = Math.max(1, p.stepCount - stepIndex)
                next.length = Math.min(max, Math.max(0.0625, length))
              }
              return next
            }),
          ),
        ),

      setStepReverse: (row, stepIndex, reverse) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (!reverse) delete next.reverse
              else next.reverse = true
              return next
            }),
          ),
        ),

      setStepTimeStretch: (row, stepIndex, stretch) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (stretch === undefined || stretch === 1) {
                delete next.timeStretch
              } else {
                next.timeStretch = Math.max(0.25, Math.min(4, stretch))
              }
              return next
            }),
          ),
        ),

      setStepEnvelope: (row, stepIndex, envelope) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (!envelope || Object.keys(envelope).length === 0) {
                delete next.envelope
              } else {
                next.envelope = envelope
              }
              return next
            }),
          ),
        ),

      setStepRoll: (row, stepIndex, roll) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (!roll) delete next.roll
              else next.roll = roll
              return next
            }),
          ),
        ),

      setStepMicroOffset: (row, stepIndex, offset) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              const next = { ...c }
              if (offset === undefined) {
                delete next.microOffset
              } else {
                // Clamp into `[0, 1)`. Values of exactly 1 would
                // cross into the next step which we don't allow —
                // callers should increment stepIndex instead.
                const clamped = Math.max(0, Math.min(0.999, offset))
                // Treat 0 as equivalent to "on grid" so we keep the
                // cell small and selectors don't see spurious
                // microOffset=0 entries.
                if (clamped === 0) delete next.microOffset
                else next.microOffset = clamped
              }
              return next
            }),
          ),
        ),

      quantizePattern: ({ subdivision, strength, scope }) =>
        set(state => {
          const pid = state.currentProjectId
          const project = state.projects[pid]
          if (!project) return state
          const focus = state.trackerFocus
          const effectiveScope =
            scope === 'focused' && !focus ? 'allKit' : scope
          // Build a predicate that selects which step rows the
          // algorithm should touch. Done here (not inside
          // `quantizeGrid`) so we can still use the shared pure
          // function for the Phase 3c record-and-quantize flow
          // where the scope is "the pad we just recorded on."
          const focusedPadIndex =
            effectiveScope === 'focused' && focus?.row.kind === 'kit'
              ? focus.row.padIndex
              : null
          const focusedMidiPadId =
            effectiveScope === 'focused' && focus?.row.kind === 'midi'
              ? focus.row.padId
              : null
          const nextSteps: StepGrid = {}
          for (const [padKey, row] of Object.entries(project.steps)) {
            const padIdx = Number(padKey)
            const shouldTouch =
              effectiveScope === 'allKit' ||
              effectiveScope === 'allIncludingMidi' ||
              focusedPadIndex === padIdx
            nextSteps[padIdx] = shouldTouch
              ? quantizeRow(row, { subdivision, strength })
              : row
          }
          const nextMidiPadSteps: Record<string, StepCell[]> = {}
          for (const [padId, row] of Object.entries(project.midiPadSteps)) {
            const shouldTouch =
              effectiveScope === 'allIncludingMidi' ||
              focusedMidiPadId === padId
            nextMidiPadSteps[padId] = shouldTouch
              ? quantizeRow(row, { subdivision, strength })
              : row
          }
          return {
            projects: {
              ...state.projects,
              [pid]: {
                ...project,
                steps: nextSteps,
                midiPadSteps: nextMidiPadSteps,
              },
            },
          }
        }),

      clearStepExtras: (row, stepIndex) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => ({ on: c.on })),
          ),
        ),

      // ── Custom MIDI pads ──
      addMidiPad: init => {
        const id = genId('midipad')
        set(state =>
          updateCurrent(state, p => {
            const nextIndex = p.midiPads.length + 1
            const def: MidiPadDef = {
              id,
              label: init?.label ?? `MIDI ${nextIndex}`,
              note: clamp127(init?.note ?? 60),
              channel: clampChannel(init?.channel ?? 1),
            }
            return {
              ...p,
              midiPads: [...p.midiPads, def],
              midiPadSteps: {
                ...p.midiPadSteps,
                [id]: Array.from({ length: p.stepCount }, () => ({
                  on: false,
                })),
              },
            }
          }),
        )
        return id
      },

      removeMidiPad: padId =>
        set(state =>
          updateCurrent(state, p => {
            const nextSteps = { ...p.midiPadSteps }
            delete nextSteps[padId]
            return {
              ...p,
              midiPads: p.midiPads.filter(mp => mp.id !== padId),
              midiPadSteps: nextSteps,
            }
          }),
        ),

      renameMidiPad: (padId, label) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            midiPads: p.midiPads.map(mp =>
              mp.id === padId ? { ...mp, label } : mp,
            ),
          })),
        ),

      setMidiPadNote: (padId, note) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            midiPads: p.midiPads.map(mp =>
              mp.id === padId ? { ...mp, note: clamp127(note) } : mp,
            ),
          })),
        ),

      setMidiPadChannel: (padId, channel) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            midiPads: p.midiPads.map(mp =>
              mp.id === padId ? { ...mp, channel: clampChannel(channel) } : mp,
            ),
          })),
        ),

      setMidiPadOutput: (padId, outputId) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            midiPads: p.midiPads.map(mp => {
              if (mp.id !== padId) return mp
              const next = { ...mp }
              if (outputId === null) delete next.outputId
              else next.outputId = outputId
              return next
            }),
          })),
        ),

      // ── Per-kit-pad MIDI routing ──
      setPadOutput: (padIndex, outputId) =>
        set(state =>
          updateCurrent(state, p => {
            const next = { ...(p.padOutputs ?? {}) }
            if (outputId === null) {
              delete next[padIndex]
            } else {
              next[padIndex] = outputId
            }
            // Drop the key entirely when empty so projects with no
            // per-pad overrides serialise without the field.
            const hasAny = Object.keys(next).length > 0
            return hasAny
              ? { ...p, padOutputs: next }
              : { ...p, padOutputs: undefined }
          }),
        ),

      // ── Keys Mode ──
      setKeysMode: patch =>
        set(state =>
          updateCurrent(state, p => {
            const prev = p.keysMode ?? DEFAULT_KEYS_MODE
            const next: KeysModeConfig = { ...prev, ...patch }
            // Clamp numeric fields to their valid ranges.
            next.rootNote = clamp127(next.rootNote)
            next.channel = clampChannel(next.channel)
            return { ...p, keysMode: next }
          }),
        ),

      toggleKeysMode: () =>
        set(state => {
          // Capture the focused pad when Keys turns ON so the fire
          // path uses a stable source voice for the whole session
          // (regardless of where the user taps next). Clear it when
          // turning OFF so a future re-activation re-captures against
          // the then-current focus.
          const capturedSource = state.focusedPad
          return updateCurrent(state, p => {
            const prev = p.keysMode ?? DEFAULT_KEYS_MODE
            const nextEnabled = !prev.enabled
            return {
              ...p,
              keysMode: {
                ...prev,
                enabled: nextEnabled,
                sourcePadIndex: nextEnabled ? capturedSource : undefined,
              },
            }
          })
        }),

      // ── Per-pad content (any kind — synth / sample / midi) ──
      //
      // Writes the new content to BOTH the current pattern's
      // `padDefs` (where `resolvePad` reads during playback) AND
      // the owning bank's `padDefs` (what the container-sync
      // round-trips to the server). Before this mirror, pad
      // assignments persisted locally but the sync loop read
      // from bank-level on load and silently lost them — samples
      // came back from the library, but the pad → slice bindings
      // were gone after any reload that went through sync.
      //
      // Bank is the semantic source of truth (pads are a bank
      // property; patterns inside a bank share the same pad
      // layout), but the pattern-level mirror is load-bearing
      // for every `selectCurrentProject`-backed consumer, so we
      // keep both in lockstep here rather than refactoring every
      // reader.
      setPadContent: (padIndex, content) =>
        set(state => {
          const curr = state.projects[state.currentProjectId]
          if (!curr) return state
          const container = state.containers[state.currentContainerId]
          if (!container) return state

          // Find the bank that owns the active pattern. Patterns
          // carry no bankId back-pointer so we scan — 4 banks max,
          // negligible cost. On miss (orphaned pattern) we fall
          // back to just updating the active pattern.
          let targetBankId: BankId | null = null
          for (const bankId of ['A', 'B', 'C', 'D'] as const) {
            if (container.banks[bankId]?.patternIds.includes(curr.id)) {
              targetBankId = bankId
              break
            }
          }

          // If no bank owns this pattern, update pattern only.
          if (!targetBankId) {
            const patternDefs = { ...(curr.padDefs ?? {}) }
            if (content === null) delete patternDefs[padIndex]
            else patternDefs[padIndex] = content
            const patternHasAny = Object.keys(patternDefs).length > 0
            return {
              projects: {
                ...state.projects,
                [state.currentProjectId]: patternHasAny
                  ? { ...curr, padDefs: patternDefs }
                  : { ...curr, padDefs: undefined },
              },
            }
          }

          // Bank-scoped write — pads belong to the bank, so every
          // pattern inside the bank should see the assignment.
          // Without this, switching patterns in the same bank
          // would snap back to stale pattern-local padDefs (the
          // bug the user reported as "assigning slices only
          // updates the specific pattern that was open"). Walk
          // every pattern in the target bank and apply the same
          // per-pad edit.
          const bank = container.banks[targetBankId]
          const bankDefs = { ...(bank.padDefs ?? {}) }
          if (content === null) delete bankDefs[padIndex]
          else bankDefs[padIndex] = content
          const bankHasAny = Object.keys(bankDefs).length > 0

          const nextProjects = { ...state.projects }
          for (const pid of bank.patternIds) {
            const p = state.projects[pid]
            if (!p) continue
            const defs = { ...(p.padDefs ?? {}) }
            if (content === null) delete defs[padIndex]
            else defs[padIndex] = content
            const hasAny = Object.keys(defs).length > 0
            nextProjects[pid] = hasAny
              ? { ...p, padDefs: defs }
              : { ...p, padDefs: undefined }
          }

          return {
            projects: nextProjects,
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                banks: {
                  ...container.banks,
                  [targetBankId]: {
                    ...bank,
                    padDefs: bankHasAny ? bankDefs : undefined,
                  },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setPadContentInBank: (bankId, padIndex, content) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container) return state
          const bank = container.banks[bankId]
          if (!bank) return state

          // Same bank-level + every-pattern-in-bank write shape as
          // setPadContent, just targeting the explicit bankId.
          const bankDefs = { ...(bank.padDefs ?? {}) }
          if (content === null) delete bankDefs[padIndex]
          else bankDefs[padIndex] = content
          const bankHasAny = Object.keys(bankDefs).length > 0

          const nextProjects = { ...state.projects }
          for (const pid of bank.patternIds) {
            const p = state.projects[pid]
            if (!p) continue
            const defs = { ...(p.padDefs ?? {}) }
            if (content === null) delete defs[padIndex]
            else defs[padIndex] = content
            const hasAny = Object.keys(defs).length > 0
            nextProjects[pid] = hasAny
              ? { ...p, padDefs: defs }
              : { ...p, padDefs: undefined }
          }

          return {
            projects: nextProjects,
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                banks: {
                  ...container.banks,
                  [bankId]: {
                    ...bank,
                    padDefs: bankHasAny ? bankDefs : undefined,
                  },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      // ── Mixer buses (Phase 2b) ──
      // padBus writes go to the current bank and are scoped to the
      // bank so different banks in the same container can route the
      // same pad index to different buses (e.g. bank A's pad 3
      // routes to "Drums" while bank B's pad 3 routes to "Melody").
      // Matches how padDefs + customSamples already scope.
      setPadBus: (padIndex, busId) => {
        const state = get()
        const container = state.containers[state.currentContainerId]
        if (!container) return
        state.setPadBusInBank(container.activeBankId, padIndex, busId)
      },
      setPadBusInBank: (bankId, padIndex, busId) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container) return state
          const bank = container.banks[bankId]
          if (!bank) return state
          const next = { ...(bank.padBus ?? {}) }
          // Store the explicit assignment. Routing to master is the
          // implicit default, so we drop the key to keep the map lean
          // — an unmapped pad always means "master."
          if (busId === MASTER_BUS_ID) delete next[padIndex]
          else next[padIndex] = busId
          const hasAny = Object.keys(next).length > 0
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                banks: {
                  ...container.banks,
                  [bankId]: {
                    ...bank,
                    padBus: hasAny ? next : undefined,
                  },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      addBus: name => {
        const id = `bus_${Math.random().toString(36).slice(2, 10)}`
        const defaults: Bus = {
          id,
          name: name || 'Bus',
          volume: 1,
          pan: 0,
        }
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container) return state
          const existing = container.buses ?? [
            { id: MASTER_BUS_ID, name: 'Master', volume: 1, pan: 0 },
          ]
          // Number unnamed buses by their position in the list so
          // the default names remain unique without forcing the user
          // to rename each one. Match to the *next* index after the
          // existing list (so "Bus 2" comes after "Bus 1").
          const finalName =
            name && name.trim()
              ? name.trim()
              : `Bus ${existing.filter(b => b.id !== MASTER_BUS_ID).length + 1}`
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: [...existing, { ...defaults, name: finalName }],
                updatedAt: new Date().toISOString(),
              },
            },
          }
        })
        return id
      },

      removeBus: busId =>
        set(state => {
          if (busId === MASTER_BUS_ID) return state
          const container = state.containers[state.currentContainerId]
          if (!container) return state
          const existing = container.buses ?? []
          if (!existing.some(b => b.id === busId)) return state
          // Re-route every bank's pads currently pointing at the
          // removed bus back to master. Leaves stale entries only
          // in bank records the user never touched — which is
          // fine because the engine already falls back to master
          // when the reference is missing.
          const nextBanks: Record<BankId, ProjectContainerBank> = {
            ...container.banks,
          }
          for (const bankId of Object.keys(nextBanks) as BankId[]) {
            const bank = nextBanks[bankId]
            if (!bank?.padBus) continue
            const cleaned: Record<number, string> = {}
            for (const [k, v] of Object.entries(bank.padBus)) {
              if (v !== busId) cleaned[Number(k)] = v
            }
            nextBanks[bankId] = {
              ...bank,
              padBus: Object.keys(cleaned).length > 0 ? cleaned : undefined,
            }
          }
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: existing.filter(b => b.id !== busId),
                banks: nextBanks,
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      renameBus: (busId, name) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container || !container.buses) return state
          const trimmed = name.trim()
          if (!trimmed) return state
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b =>
                  b.id === busId ? { ...b, name: trimmed } : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setBusVolume: (busId, volume) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container || !container.buses) return state
          const clamped = Math.max(0, Math.min(1, volume))
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b =>
                  b.id === busId ? { ...b, volume: clamped } : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setBusPan: (busId, pan) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container || !container.buses) return state
          const clamped = Math.max(-1, Math.min(1, pan))
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b =>
                  b.id === busId ? { ...b, pan: clamped } : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setBusOutputChannel: (busId, outputChannelStart) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container || !container.buses) return state
          // Clamp to non-negative. Actual max is bounded by the
          // active output device's channel count, which the UI
          // enforces via the picker; storing an out-of-range value
          // is harmless because the engine falls back to ch 0
          // when the slot doesn't exist on the device.
          const clamped = Math.max(0, Math.floor(outputChannelStart))
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b =>
                  b.id === busId
                    ? {
                        ...b,
                        outputChannelStart: clamped === 0 ? undefined : clamped,
                      }
                    : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      addBusEffect: (busId, kind, pluginId) => {
        const id = `fx_${Math.random().toString(36).slice(2, 10)}`
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container?.buses) return state
          // Master bus is synthesised by `selectCurrentBuses` when
          // missing from the stored list, so UI shows it even before
          // the user has added any effects to it. The first write
          // (addBusEffect, updateBusEffect, etc.) has to upsert a real
          // entry — otherwise `.map` has no master to match against
          // and the patch silently no-ops (bug reported 2026-04-22:
          // "can't add plugins to Master from the Inspector FX chain").
          const hasMasterRow = container.buses.some(b => b.id === MASTER_BUS_ID)
          const bases =
            busId === MASTER_BUS_ID && !hasMasterRow
              ? [
                  { id: MASTER_BUS_ID, name: 'Master', volume: 1, pan: 0 },
                  ...container.buses,
                ]
              : container.buses
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: bases.map(b =>
                  b.id === busId
                    ? {
                        ...b,
                        effects: [
                          ...(b.effects ?? []),
                          {
                            id,
                            kind,
                            enabled: true,
                            // Only attach pluginId when kind is
                            // 'plugin' so legacy kinds stay clean
                            // on disk.
                            ...(kind === 'plugin' && pluginId
                              ? { pluginId }
                              : {}),
                          },
                        ],
                      }
                    : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        })
        return id
      },

      removeBusEffect: (busId, effectId) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container?.buses) return state
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b =>
                  b.id === busId
                    ? {
                        ...b,
                        effects: (b.effects ?? []).filter(
                          e => e.id !== effectId,
                        ),
                      }
                    : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      reorderBusEffects: (busId, ids) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container?.buses) return state
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b => {
                  if (b.id !== busId || !b.effects) return b
                  const index = new Map(b.effects.map(e => [e.id, e]))
                  // Preserve original order for any ids not in the
                  // reorder list — defensive in case the caller
                  // forgets to include every id.
                  const reordered = ids
                    .map(id => index.get(id))
                    .filter((e): e is BusEffect => e !== undefined)
                  const leftover = b.effects.filter(e => !ids.includes(e.id))
                  return { ...b, effects: [...reordered, ...leftover] }
                }),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      updateBusEffect: (busId, effectId, patch) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container?.buses) return state
          // Master bus is synthesised on read (see `selectCurrentBuses`);
          // when it hasn't been persisted yet, `.map` finds no row to
          // match and the patch silently no-ops. If the caller is
          // trying to patch an effect that doesn't exist on any stored
          // bus row, we bail cleanly — but if the caller targets the
          // synthesised master, skip the bail and let the no-op
          // happen (there's nothing to patch there anyway; the effect
          // must have been added first via addBusEffect).
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                buses: container.buses.map(b =>
                  b.id === busId && b.effects
                    ? {
                        ...b,
                        effects: b.effects.map(e =>
                          e.id === effectId
                            ? {
                                ...e,
                                ...patch,
                                // Merge params rather than replace so
                                // partial patches ({ params: { time: 0.3 } })
                                // don't wipe other keys.
                                params: patch.params
                                  ? { ...e.params, ...patch.params }
                                  : e.params,
                              }
                            : e,
                        ),
                      }
                    : b,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      addInputSource: init => {
        const id = `src_${Math.random().toString(36).slice(2, 10)}`
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container) return state
          const initChannelCount = init?.channelCount === 2 ? 2 : undefined
          const next: InputSource = {
            id,
            deviceId: init?.deviceId ?? '',
            channelIndex: Math.max(0, init?.channelIndex ?? 0),
            busId: init?.busId ?? MASTER_BUS_ID,
            // Only persist the non-default channelCount. Keeps older
            // patterns clean when they're re-saved + keeps the
            // field optional for pre-P2 stored sources.
            ...(initChannelCount ? { channelCount: initChannelCount } : {}),
          }
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                inputSources: [...(container.inputSources ?? []), next],
                updatedAt: new Date().toISOString(),
              },
            },
          }
        })
        return id
      },

      removeInputSource: sourceId =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container?.inputSources) return state
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                inputSources: container.inputSources.filter(
                  s => s.id !== sourceId,
                ),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      updateInputSource: (sourceId, patch) =>
        set(state => {
          const container = state.containers[state.currentContainerId]
          if (!container?.inputSources) return state
          return {
            containers: {
              ...state.containers,
              [container.id]: {
                ...container,
                inputSources: container.inputSources.map(s => {
                  if (s.id !== sourceId) return s
                  const merged: InputSource = {
                    ...s,
                    ...patch,
                    channelIndex:
                      patch.channelIndex !== undefined
                        ? Math.max(0, patch.channelIndex)
                        : s.channelIndex,
                  }
                  // Persist channelCount only for the stereo case to
                  // keep the field optional on the serialized shape.
                  if (patch.channelCount === 2) {
                    merged.channelCount = 2
                  } else if (patch.channelCount === 1) {
                    delete merged.channelCount
                  }
                  return merged
                }),
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      // ── Custom samples (Plan 2b legacy; routes through
      //     setPadContent so we don't dual-write) ──
      setCustomSample: (padIndex, ref) => {
        if (ref === null) {
          get().setPadContent(padIndex, null)
          return
        }
        // Migrate the call site to a sample-kind PadContent. The
        // midiNote default follows the chromatic-by-pad convention
        // (48 + padIndex, i.e. C3 at pad 0) so legacy callers get
        // the same MIDI-in matching as PadEditor-driven additions.
        get().setPadContent(padIndex, {
          kind: 'sample',
          sampleId: ref.sampleId,
          s3Key: ref.s3Key,
          name: ref.name,
          midiNote: clamp127(48 + padIndex),
        })
      },

      // ── Multi-project ──
      addProject: name => {
        const state = get()
        // Scan both "Project N" and "Pattern N" — during the
        // Projects→Patterns rename the user may accumulate both
        // prefixes and we don't want the auto-namer to pick a
        // number that collides with an existing tab.
        const existingNumbers = Object.values(state.projects)
          .map(p => p.name.match(/^(?:Project|Pattern)\s+(\d+)$/i)?.[1])
          .filter((n): n is string => Boolean(n))
          .map(Number)
        const nextN = existingNumbers.length
          ? Math.max(...existingNumbers) + 1
          : 1
        const finalName = name ?? `Pattern ${nextN}`
        const id = genId('p')
        const project = createProject(id, finalName)
        set({ projects: { ...state.projects, [id]: project } })
        return id
      },

      selectProject: id =>
        set(state => {
          if (!state.projects[id]) return state
          return {
            currentProjectId: id,
            isPlaying: false,
            currentStep: -1,
            focusedPad: 0,
            trackerFocus: null,
          }
        }),

      renameProject: (id, name) =>
        set(state => {
          const p = state.projects[id]
          if (!p) return state
          return {
            projects: { ...state.projects, [id]: { ...p, name } },
          }
        }),

      deleteProject: id =>
        set(state => {
          const remaining = { ...state.projects }
          delete remaining[id]
          // Prune song entries referencing the deleted project so song
          // playback doesn't jump to missing ids.
          const prunedSong = state.song.filter(e => e.projectId !== id)
          if (Object.keys(remaining).length === 0) {
            const seed = createProject(DEFAULT_PROJECT_ID, 'Project 1')
            return {
              projects: { [DEFAULT_PROJECT_ID]: seed },
              currentProjectId: DEFAULT_PROJECT_ID,
              isPlaying: false,
              currentStep: -1,
              focusedPad: 0,
              trackerFocus: null,
              song: prunedSong,
              songCursor: null,
            }
          }
          const nextCurrent =
            state.currentProjectId === id
              ? Object.keys(remaining)[0]
              : state.currentProjectId
          return {
            projects: remaining,
            currentProjectId: nextCurrent,
            isPlaying: false,
            currentStep: -1,
            focusedPad: 0,
            trackerFocus: null,
            song: prunedSong,
            songCursor: null,
          }
        }),

      resetCurrentProject: () =>
        set(state => {
          const curr = state.projects[state.currentProjectId]
          if (!curr) return state
          const reset = createProject(curr.id, curr.name, curr.stepCount)
          return {
            projects: { ...state.projects, [curr.id]: reset },
            isPlaying: false,
            currentStep: -1,
            trackerFocus: null,
          }
        }),

      hydrateFromRemote: remote =>
        set(state => {
          // Server is authoritative when it has any projects. The
          // previous implementation merged {local, ...remote}, which
          // meant every fresh browser's "default" seed survived
          // hydrate, got pushed back by the sync loop, and piled up
          // a new "Project 1" on the server each time — the 17/16
          // duplicate blow-up we're cleaning up. Now: if remote has
          // ≥1 project, replace the map. If remote is empty, keep
          // local (user has offline drafts worth preserving).
          if (remote.length > 0) {
            const next: Record<string, Project> = {}
            for (const p of remote) {
              if (p && p.id) next[p.id] = p
            }
            const ids = Object.keys(next)
            if (ids.length === 0) return state
            // Preserve any local project the server doesn't know about
            // yet, EXCEPT the untouched default seed — the seed is
            // never worth keeping once real projects exist.
            for (const [id, p] of Object.entries(state.projects)) {
              if (id in next) continue
              if (isUntouchedDefaultProject(p)) continue
              next[id] = p
            }
            const nextIds = Object.keys(next)
            const nextCurrent = next[state.currentProjectId]
              ? state.currentProjectId
              : nextIds[0]
            return { projects: next, currentProjectId: nextCurrent }
          }
          // Remote empty: keep the local state. If local is also
          // empty, seed a default so the editor has something to
          // open — matches the previous behaviour for brand-new
          // users on their first device.
          const localIds = Object.keys(state.projects)
          if (localIds.length === 0) {
            return {
              projects: defaultProjects(),
              currentProjectId: DEFAULT_PROJECT_ID,
            }
          }
          return state
        }),

      pruneDuplicates: () => {
        const state = get()
        const projects = Object.values(state.projects)
        if (projects.length <= 1) return []
        // Group by content hash; keep the first occurrence (Object.values
        // iteration order matches insertion, so the oldest project in
        // the map wins) and drop the rest.
        const byHash = new Map<string, Project>()
        const toDelete: string[] = []
        for (const p of projects) {
          const hash = serializeProjectForDedupe(p)
          const prior = byHash.get(hash)
          if (!prior) {
            byHash.set(hash, p)
          } else {
            // Keep the project whose id matches the active selection
            // if possible, so pruning doesn't yank the user out of the
            // tab they're currently editing.
            if (p.id === state.currentProjectId) {
              toDelete.push(prior.id)
              byHash.set(hash, p)
            } else {
              toDelete.push(p.id)
            }
          }
        }
        if (toDelete.length === 0) return []
        const next = { ...state.projects }
        for (const id of toDelete) delete next[id]
        const keptIds = Object.keys(next)
        const nextCurrent = next[state.currentProjectId]
          ? state.currentProjectId
          : (keptIds[0] ?? DEFAULT_PROJECT_ID)
        // Prune song entries referencing any removed project so song
        // playback doesn't jump to a missing id.
        const deletedSet = new Set(toDelete)
        const prunedSong = state.song.filter(e => !deletedSet.has(e.projectId))
        set({
          projects: keptIds.length > 0 ? next : defaultProjects(),
          currentProjectId:
            keptIds.length > 0 ? nextCurrent : DEFAULT_PROJECT_ID,
          song: prunedSong,
          songCursor: null,
        })
        return toDelete
      },

      setLayoutMode: mode =>
        set(state => ({
          layoutMode: mode,
          // Tracker focus is meaningless in other layouts; drop it on
          // mode change so re-entering Tracker gives a fresh slate.
          trackerFocus: mode === 'tracker' ? state.trackerFocus : null,
        })),

      setFocusedPad: padIndex => set({ focusedPad: padIndex }),

      setTrackerFocus: focus => set({ trackerFocus: focus }),

      setPlaying: playing =>
        set(state => ({
          isPlaying: playing,
          currentStep: playing ? 0 : -1,
          // Starting song-mode playback primes the cursor at the first
          // entry; stopping clears it so the next play starts from 0.
          songCursor: playing
            ? state.songMode && state.song.length > 0
              ? {
                  entryIndex: 0,
                  repeatsRemaining: Math.max(1, state.song[0].repeats),
                }
              : null
            : null,
          // Stopping playback discards any queued bank/pattern switch
          // — the user will decide again next time they hit play.
          queuedBankId: playing ? state.queuedBankId : null,
          queuedPatternId: playing ? state.queuedPatternId : null,
        })),

      setCurrentStep: step => set({ currentStep: step }),

      setRecordMode: mode => set({ recordMode: mode }),

      cycleRecordMode: () =>
        set(state => {
          const next: SequencerState['recordMode'] =
            state.recordMode === 'off'
              ? 'record'
              : state.recordMode === 'record'
                ? 'overdub'
                : 'off'
          return { recordMode: next }
        }),

      recordCell: (row, stepIndex, opts) =>
        set(state =>
          updateCurrent(state, p =>
            updateCell(p, row, stepIndex, c => {
              // Overdub preserves rich-cell fields on an already-on
              // cell (user iterates a pattern in passes, each pass
              // layering velocity / timing nuances on top of prior
              // takes). Record mode starts from scratch — the new
              // hit replaces whatever was there, closest to how a
              // hardware sequencer treats re-recording a step.
              const base: StepCell = opts.overdub && c.on ? c : { on: true }
              const velocity =
                opts.velocity !== undefined
                  ? Math.max(0, Math.min(1, opts.velocity))
                  : undefined
              const microOffset =
                opts.microOffset !== undefined &&
                opts.microOffset > 0 &&
                opts.microOffset < 1
                  ? opts.microOffset
                  : undefined
              const next: StepCell = {
                ...base,
                on: true,
              }
              if (velocity !== undefined) next.velocity = velocity
              if (microOffset !== undefined) next.microOffset = microOffset
              else if (!opts.overdub) delete next.microOffset
              return next
            }),
          ),
        ),

      setFxActive: (fxId, active) =>
        set(state => ({
          fxState: { ...state.fxState, [fxId]: active },
        })),

      setFxLevel: (fxId, amount) =>
        set(state => ({
          fxLevels: {
            ...state.fxLevels,
            [fxId]: Math.max(0, Math.min(1, amount)),
          },
        })),

      setPerfFilterMode: mode => set({ perfFilterMode: mode }),

      setFxLatchMode: enabled =>
        set({
          fxLatchMode: enabled,
          // Latch + Hold are mutually exclusive: enabling one
          // disables the other so the FX rail has exactly one
          // active perform mode at a time.
          ...(enabled ? { fxHoldMode: false } : {}),
        }),

      setFxHoldMode: enabled =>
        set({
          fxHoldMode: enabled,
          ...(enabled ? { fxLatchMode: false } : {}),
        }),

      // ── Scrambler ──
      setScramblerParams: patch =>
        set(state => ({
          scramblerParams: { ...state.scramblerParams, ...patch },
          // Tweaking any param clears the "recalled preset" badge
          // so users see they're now on unsaved territory.
          activeScramblerSnapshotId: null,
        })),

      bumpScramblerSeed: () =>
        set(state => ({
          scramblerParams: {
            ...state.scramblerParams,
            enabled: true,
            scrambleSeed: (state.scramblerParams.scrambleSeed + 1) >>> 0,
          },
          activeScramblerSnapshotId: null,
        })),

      saveScramblerSnapshot: name => {
        const state = get()
        const project = selectCurrentProject(state)
        if (!project) return null
        const id = 'jsc_' + Math.random().toString(36).slice(2, 10)
        const label =
          name ??
          `Scramble ${String.fromCharCode(
            65 + (project.scramblerSnapshots?.length ?? 0),
          )}`
        const snapshot: ScramblerSnapshot = {
          id,
          name: label,
          params: { ...state.scramblerParams },
          createdAt: new Date().toISOString(),
        }
        set(s => ({
          ...updateCurrent(s, p => ({
            ...p,
            scramblerSnapshots: [...(p.scramblerSnapshots ?? []), snapshot],
          })),
          activeScramblerSnapshotId: id,
        }))
        return id
      },

      recallScramblerSnapshot: id => {
        const state = get()
        const project = selectCurrentProject(state)
        if (!project) return
        const preset = project.scramblerSnapshots?.find(s => s.id === id)
        if (!preset) return
        set({
          scramblerParams: { ...preset.params },
          activeScramblerSnapshotId: id,
        })
      },

      deleteScramblerSnapshot: id =>
        set(state => ({
          ...updateCurrent(state, p => ({
            ...p,
            scramblerSnapshots: (p.scramblerSnapshots ?? []).filter(
              s => s.id !== id,
            ),
          })),
          activeScramblerSnapshotId:
            state.activeScramblerSnapshotId === id
              ? null
              : state.activeScramblerSnapshotId,
        })),

      renameScramblerSnapshot: (id, name) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            scramblerSnapshots: (p.scramblerSnapshots ?? []).map(s =>
              s.id === id ? { ...s, name } : s,
            ),
          })),
        ),

      printScramblerToPattern: () => {
        const state = get()
        const project = state.projects[state.currentProjectId]
        if (!project) return null
        const params = state.scramblerParams
        if (!params.enabled) return null

        // Find the source pattern's home bank so the new pattern
        // lands in the same bank — matches the user-facing contract
        // that Print lands on "the next empty slot in the same
        // bank" rather than an arbitrary bank.
        const container = state.containers[state.currentContainerId]
        if (!container) return null
        let sourceBankId: BankId | null = null
        for (const bankId of ['A', 'B', 'C', 'D'] as const) {
          if (container.banks[bankId]?.patternIds.includes(project.id)) {
            sourceBankId = bankId
            break
          }
        }
        if (!sourceBankId) return null
        const bankPadBus: Record<number, string> =
          container.banks[sourceBankId]?.padBus ?? {}

        // Target length: 1× for the default (bars=1), 2×/4×/8× when
        // the user has dialled a longer form. Gives each A/A'/B
        // slot its own bar in the printed pattern.
        const bars = params.scramblerBars
        const sourceLen = project.stepCount
        const newLen = Math.min(64, sourceLen * bars) as StepCount

        // Build each row. In-scope pads walk through the Scrambler
        // step-by-step with the new `stepIndex` spanning the full
        // printed length, so the form slots shift across bars the
        // same way they would at live playback. Out-of-scope pads
        // tile their source row across the printed length.
        const newSteps: StepCell[][] = []
        for (let padIdx = 0; padIdx < PADS_PER_PROJECT; padIdx++) {
          const sourceRow = project.steps[padIdx] ?? []
          const padBusId = bankPadBus[padIdx]
          const inScope = isPadInScramblerScope(params.target, padIdx, padBusId)
          const out: StepCell[] = []
          for (let i = 0; i < newLen; i++) {
            const rawCell = sourceRow[i % sourceLen] ?? { on: false }
            if (!inScope || !rawCell.on) {
              out.push({ ...rawCell })
              continue
            }
            const scrambled = applyScramblerParams(
              rawCell,
              params,
              padIdx,
              i,
              sourceLen,
              sourceRow,
              padBusId,
            )
            out.push(scrambled ?? { on: false })
          }
          newSteps.push(out)
        }

        // Auto-name "Scramble of X 1", "Scramble of X 2", ...
        const bank = container.banks[sourceBankId]
        const baseName = `Scramble of ${project.name}`
        const escaped = baseName.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
        const existingNumbers = bank.patternIds
          .map(pid => state.projects[pid]?.name ?? '')
          .map(n => {
            const match = n.match(new RegExp(`^${escaped}(?:\\s+(\\d+))?$`))
            return match ? (match[1] ? Number(match[1]) : 1) : null
          })
          .filter((n): n is number => n !== null)
        const nextN = existingNumbers.length
          ? Math.max(...existingNumbers) + 1
          : 1
        const name = nextN === 1 ? baseName : `${baseName} ${nextN}`

        const newPatternId = genId('p')
        const newPattern: Project = {
          ...project,
          id: newPatternId,
          name,
          stepCount: newLen,
          steps: newSteps,
          // Clear pattern-level extras that don't belong on a
          // printed copy.
          scramblerSnapshots: undefined,
          notes: undefined,
          automation: undefined,
        }

        set({
          projects: { ...state.projects, [newPatternId]: newPattern },
          currentProjectId: newPatternId,
          containers: {
            ...state.containers,
            [state.currentContainerId]: {
              ...container,
              banks: {
                ...container.banks,
                [sourceBankId]: {
                  ...bank,
                  patternIds: [...bank.patternIds, newPatternId],
                  activePatternId: newPatternId,
                },
              },
              updatedAt: new Date().toISOString(),
            },
          },
        })
        return newPatternId
      },

      // ── Piano roll (Wave 5) ──
      addPatternNote: note => {
        const id = `note-${Math.random().toString(36).slice(2, 10)}`
        set(prev =>
          updateCurrent(prev, p => ({
            ...p,
            notes: [...(p.notes ?? []), { id, ...note }],
          })),
        )
        return id
      },

      removePatternNote: noteId =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            notes: (p.notes ?? []).filter(n => n.id !== noteId),
          })),
        ),

      clearPatternNotes: () =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            notes: [],
          })),
        ),

      updatePatternNote: (noteId, patch) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            notes: (p.notes ?? []).map(n =>
              n.id === noteId ? { ...n, ...patch } : n,
            ),
          })),
        ),

      // ── Automation (Wave 5) ──
      addAutomationLane: paramPath => {
        const id = `lane-${Math.random().toString(36).slice(2, 10)}`
        set(prev =>
          updateCurrent(prev, p => ({
            ...p,
            automation: [
              ...(p.automation ?? []),
              { id, paramPath, points: [], enabled: true },
            ],
          })),
        )
        return id
      },

      removeAutomationLane: laneId =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: (p.automation ?? []).filter(l => l.id !== laneId),
          })),
        ),

      clearAutomationLanes: () =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: [],
          })),
        ),

      duplicateAutomationLane: laneId => {
        const state = get()
        const container = state.containers[state.currentContainerId]
        const bank = container?.banks[container?.activeBankId ?? '']
        const projectId = state.currentProjectId
        const project = state.projects[projectId]
        const src = project?.automation?.find(l => l.id === laneId)
        if (!src) return null
        const newId = `lane-${Math.random().toString(36).slice(2, 10)}`
        set(prev =>
          updateCurrent(prev, p => ({
            ...p,
            automation: [
              ...(p.automation ?? []),
              {
                ...src,
                id: newId,
                // Deep-copy points so later edits on either lane
                // don't mutate the other's array.
                points: src.points.map(pt => ({ ...pt })),
              },
            ],
          })),
        )
        // Reference `bank` so the linter doesn't drop the helper
        // read above — keeps the branch future-proof for a
        // bank-scoped duplication variant.
        void bank
        return newId
      },

      setAutomationLaneEnabled: (laneId, enabled) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: (p.automation ?? []).map(l =>
              l.id === laneId ? { ...l, enabled } : l,
            ),
          })),
        ),

      setAutomationLaneRange: (laneId, min, max) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: (p.automation ?? []).map(l =>
              l.id === laneId ? { ...l, min, max } : l,
            ),
          })),
        ),

      addAutomationPoint: (laneId, point) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: (p.automation ?? []).map(l => {
              if (l.id !== laneId) return l
              const next = [...l.points, point].sort((a, b) => a.tick - b.tick)
              return { ...l, points: next }
            }),
          })),
        ),

      removeAutomationPointAtTick: (laneId, tick, toleranceTicks = 12) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: (p.automation ?? []).map(l => {
              if (l.id !== laneId) return l
              // Find the closest point within tolerance and drop
              // exactly that one. Multiple points at the same tick
              // would be unusual but the filter handles it.
              let bestIdx = -1
              let bestDist = Infinity
              l.points.forEach((pt, i) => {
                const d = Math.abs(pt.tick - tick)
                if (d < bestDist) {
                  bestDist = d
                  bestIdx = i
                }
              })
              if (bestIdx === -1 || bestDist > toleranceTicks) return l
              return {
                ...l,
                points: l.points.filter((_, i) => i !== bestIdx),
              }
            }),
          })),
        ),

      moveAutomationPoint: (laneId, fromTick, to) =>
        set(state =>
          updateCurrent(state, p => ({
            ...p,
            automation: (p.automation ?? []).map(l => {
              if (l.id !== laneId) return l
              // Identify by closest-tick match (same tolerance
              // semantics as remove). If the caller's drag start
              // mutated the tick beyond the tolerance mid-drag,
              // the closest-remaining point wins; good enough
              // for interactive use.
              let bestIdx = -1
              let bestDist = Infinity
              l.points.forEach((pt, i) => {
                const d = Math.abs(pt.tick - fromTick)
                if (d < bestDist) {
                  bestDist = d
                  bestIdx = i
                }
              })
              if (bestIdx === -1) return l
              const next = [...l.points]
              next[bestIdx] = { tick: to.tick, value: to.value }
              next.sort((a, b) => a.tick - b.tick)
              return { ...l, points: next }
            }),
          })),
        ),

      setConformToProjectBpm: enabled =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c || c.conformToProjectBpm === enabled) return state
          return {
            containers: {
              ...state.containers,
              [c.id]: { ...c, conformToProjectBpm: enabled },
            },
          }
        }),

      // ── Song mode ──
      setSongMode: enabled =>
        set(state => {
          const nextState: Partial<SequencerState> = {
            songMode: enabled,
            songCursor: null,
            // Entering song mode: stage scene 0 so the scheduler has
            // something to play on the next `Play`. Leaving: clear
            // scene pointer + per-bank stops so returning to plain
            // pattern playback isn't held up by stale mutes.
            currentSceneIndex: enabled ? (state.currentSceneIndex ?? 0) : null,
            bankMuted: enabled
              ? state.bankMuted
              : { A: false, B: false, C: false, D: false },
          }
          if (enabled) {
            const c = state.containers[state.currentContainerId]
            if (c && c.scenes.length === 0) {
              // Auto-seed one scene on first entry so the grid isn't
              // empty. Bank A gets the active pattern so the user
              // hears something familiar immediately.
              nextState.containers = {
                ...state.containers,
                [state.currentContainerId]: {
                  ...c,
                  scenes: [
                    {
                      id: genId('scene'),
                      name: '',
                      lengthSteps: 16,
                      cells: {
                        A: c.banks.A.activePatternId || null,
                        B: null,
                        C: null,
                        D: null,
                      },
                    },
                  ],
                  updatedAt: new Date().toISOString(),
                },
              }
            }
          }
          return nextState
        }),

      addSongEntry: (projectId, repeats = 1) =>
        set(state => ({
          song: [
            ...state.song,
            {
              id: genId('song'),
              projectId,
              repeats: Math.max(1, Math.round(repeats)),
            },
          ],
        })),

      removeSongEntry: entryId =>
        set(state => ({
          song: state.song.filter(e => e.id !== entryId),
          songCursor: null,
        })),

      setSongRepeats: (entryId, repeats) =>
        set(state => ({
          song: state.song.map(e =>
            e.id === entryId
              ? { ...e, repeats: Math.max(1, Math.round(repeats)) }
              : e,
          ),
        })),

      moveSongEntry: (entryId, direction) =>
        set(state => {
          const idx = state.song.findIndex(e => e.id === entryId)
          if (idx < 0) return state
          const target = idx + direction
          if (target < 0 || target >= state.song.length) return state
          const next = [...state.song]
          const [moved] = next.splice(idx, 1)
          next.splice(target, 0, moved)
          return { song: next }
        }),

      setSongCursor: cursor => set({ songCursor: cursor }),

      // ── Scenes × Banks (Ableton-session song mode) ──
      addScene: () =>
        set(state =>
          patchCurrentContainer(state, c => ({
            ...c,
            scenes: [
              ...c.scenes,
              {
                id: genId('scene'),
                name: '',
                lengthSteps: 16,
                cells: { A: null, B: null, C: null, D: null },
              },
            ],
          })),
        ),
      removeScene: sceneId =>
        set(state =>
          patchCurrentContainer(state, c => ({
            ...c,
            scenes: c.scenes.filter(s => s.id !== sceneId),
          })),
        ),
      renameScene: (sceneId, name) =>
        set(state =>
          patchCurrentContainer(state, c => ({
            ...c,
            scenes: c.scenes.map(s => (s.id === sceneId ? { ...s, name } : s)),
          })),
        ),
      setSceneLengthSteps: (sceneId, lengthSteps) =>
        set(state =>
          patchCurrentContainer(state, c => ({
            ...c,
            scenes: c.scenes.map(s =>
              s.id === sceneId
                ? {
                    ...s,
                    lengthSteps: Math.max(1, Math.round(lengthSteps)),
                  }
                : s,
            ),
          })),
        ),
      moveScene: (sceneId, direction) =>
        set(state =>
          patchCurrentContainer(state, c => {
            const idx = c.scenes.findIndex(s => s.id === sceneId)
            if (idx < 0) return c
            const target = idx + direction
            if (target < 0 || target >= c.scenes.length) return c
            const next = [...c.scenes]
            const [moved] = next.splice(idx, 1)
            next.splice(target, 0, moved)
            return { ...c, scenes: next }
          }),
        ),
      setSceneCell: (sceneId, bankId, patternId) =>
        set(state => {
          // Tapping a new cell in a muted bank implicitly unmutes
          // it — the user's intent is "play this pattern now", so
          // the Stop button shouldn't keep gating the column.
          const base = patchCurrentContainer(state, c => ({
            ...c,
            scenes: c.scenes.map(s => {
              if (s.id !== sceneId) return s
              const nextCells = { ...s.cells, [bankId]: patternId }
              // Scene length must be at least as long as the longest
              // pattern in the scene AND a clean multiple of it,
              // otherwise the scheduler wraps at scene.lengthSteps
              // before the pattern finishes and the user hears the
              // first half of a 32-step pattern played twice inside
              // a 32-step scene (scene was 16 before the assignment,
              // pattern is 32; 16 % 32 = 16 → pattern steps 16–31
              // never fire). Bump to a clean multiple of the new
              // longest-pattern step count so this can't happen.
              const bankKeys: BankId[] = ['A', 'B', 'C', 'D']
              const longestStepCount = Math.max(
                16,
                ...bankKeys.map(b => {
                  const pid = nextCells[b]
                  const p = pid ? state.projects[pid] : null
                  return p?.stepCount ?? 0
                }),
              )
              const multiplier = Math.max(
                1,
                Math.round(s.lengthSteps / longestStepCount),
              )
              const normalizedLength = longestStepCount * multiplier
              return {
                ...s,
                cells: nextCells,
                lengthSteps: normalizedLength,
              }
            }),
          }))
          if (!state.bankMuted[bankId]) return base
          return {
            ...base,
            bankMuted: { ...state.bankMuted, [bankId]: false },
          }
        }),
      selectScene: sceneIndex =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return { currentSceneIndex: sceneIndex }
          if (
            sceneIndex !== null &&
            (sceneIndex < 0 || sceneIndex >= c.scenes.length)
          ) {
            return { currentSceneIndex: null }
          }
          return { currentSceneIndex: sceneIndex }
        }),
      setBankMuted: (bankId, muted) =>
        set(state => ({
          bankMuted: { ...state.bankMuted, [bankId]: muted },
        })),

      setPadMuted: (bankId, padIndex, muted) =>
        set(state => {
          const bank = state.padMuted[bankId] ?? {}
          const nextMuted = muted ?? !bank[padIndex]
          const nextBank = { ...bank }
          // Clean up falsey entries so the map stays sparse — it
          // serialises smaller and gives `Object.keys()` a true
          // "these pads are muted" list downstream without .filter().
          if (nextMuted) nextBank[padIndex] = true
          else delete nextBank[padIndex]
          return {
            padMuted: { ...state.padMuted, [bankId]: nextBank },
          }
        }),

      // ── Queued switches (downbeat-aligned) ──
      queueBankSwitch: bankId => set({ queuedBankId: bankId }),
      queuePatternSwitch: (bankId, patternId) =>
        set({ queuedPatternId: { bankId, patternId } }),
      cancelQueuedSwitches: () =>
        set({ queuedBankId: null, queuedPatternId: null }),
      applyQueuedSwitches: () => {
        const state = get()
        const { queuedBankId, queuedPatternId } = state
        if (!queuedBankId && !queuedPatternId) return
        // Apply bank first (it may change the active pattern), then
        // pattern on top (if the user queued both, the pattern wins).
        if (queuedBankId) state.selectBank(queuedBankId)
        if (queuedPatternId) {
          state.setActivePatternInBank(
            queuedPatternId.bankId,
            queuedPatternId.patternId,
          )
        }
        set({ queuedBankId: null, queuedPatternId: null })
      },

      // ── Container sync bookkeeping ──
      recordContainerSync: (localId, serverId, version) =>
        set(state => ({
          containerSyncState: {
            ...state.containerSyncState,
            [localId]: { serverId, version },
          },
        })),

      forgetContainerSync: localId =>
        set(state => {
          if (!(localId in state.containerSyncState)) return state
          const next = { ...state.containerSyncState }
          delete next[localId]
          return { containerSyncState: next }
        }),

      remapContainerId: (oldId, newId, version) =>
        set(state => {
          const container = state.containers[oldId]
          if (!container || oldId === newId) return state
          const nextContainers = { ...state.containers }
          delete nextContainers[oldId]
          nextContainers[newId] = { ...container, id: newId, version }
          const nextSync = { ...state.containerSyncState }
          delete nextSync[oldId]
          nextSync[newId] = { serverId: newId, version }
          return {
            containers: nextContainers,
            currentContainerId:
              state.currentContainerId === oldId
                ? newId
                : state.currentContainerId,
            containerSyncState: nextSync,
          }
        }),

      // ── Container / Bank actions (Phase 2 scaffolding) ──
      //
      // These operate on the new `containers` layer. Existing
      // consumers still use the flat `projects` map via
      // `selectCurrentProject`; the container layer wraps them.
      //
      // Until Phase 3 lands the bank-aware UI, most of these are
      // just state mutations — no side effects on the flat pattern
      // map beyond `addPatternToBank` which creates a new Pattern
      // there too.

      addContainer: name => {
        const state = get()
        const existingNumbers = Object.values(state.containers)
          .map(
            c =>
              // Guard: a malformed container (hydrated from an old
              // server blob or a migration gap) may arrive without a
              // `name` — don't crash the whole route over that.
              c.name?.match(/^(?:Project|Untitled project)\s*(\d*)$/i)?.[1],
          )
          .filter((n): n is string => n != null)
          .map(n => (n === '' ? 1 : Number(n)))
        const nextN = existingNumbers.length
          ? Math.max(...existingNumbers) + 1
          : 1
        const finalName = name ?? `Project ${nextN}`
        const id = genId('proj')
        // Seed with one fresh pattern so the new project isn't empty.
        const firstPatternId = genId('p')
        const firstPattern = createProject(firstPatternId, 'Pattern 1')
        set({
          projects: { ...state.projects, [firstPatternId]: firstPattern },
          containers: {
            ...state.containers,
            [id]: createEmptyContainer(id, finalName, firstPatternId),
          },
          currentContainerId: id,
          currentProjectId: firstPatternId,
        })
        return id
      },

      insertServerContainer: ({ id, name, version }) => {
        const state = get()
        // Seed a first pattern locally so the editor has something
        // to render against on mount. The empty-banks payload the
        // create endpoint sends matches this exactly minus the
        // pattern — so when the sync hook reconciles, the hash will
        // differ (hash includes the new pattern) which is fine, it
        // just means the first edit PUT includes the seeded pattern.
        const firstPatternId = genId('p')
        const firstPattern = createProject(firstPatternId, 'Pattern 1')
        set({
          projects: { ...state.projects, [firstPatternId]: firstPattern },
          containers: {
            ...state.containers,
            [id]: createEmptyContainer(id, name, firstPatternId),
          },
          currentContainerId: id,
          currentProjectId: firstPatternId,
          // Record sync atomically with the insert — if we let the
          // push loop run between the insert and `recordContainerSync`,
          // it would see a local container with no syncInfo and POST
          // again, which is exactly the duplicate-row bug.
          containerSyncState: {
            ...state.containerSyncState,
            [id]: { serverId: id, version },
          },
        })
      },

      selectContainer: containerId =>
        set(state => {
          // Optimistic path: if the id isn't in the local store yet
          // (user clicked a server-only project before sync hydrated,
          // or opened /projects/jam for the first time), still update
          // currentContainerId so the editor can resolve it once the
          // sync hook populates the map. Previously this bailed and
          // the user got bounced back to the list.
          const c = state.containers[containerId]
          if (!c) {
            return {
              currentContainerId: containerId,
              isPlaying: false,
              currentStep: -1,
              focusedPad: 0,
              trackerFocus: null,
            }
          }
          const bank = c.banks[c.activeBankId]
          const patternId =
            bank?.activePatternId ||
            bank?.patternIds[0] ||
            state.currentProjectId
          return {
            currentContainerId: containerId,
            currentProjectId: patternId,
            isPlaying: false,
            currentStep: -1,
            focusedPad: 0,
            trackerFocus: null,
          }
        }),

      renameContainer: (containerId, newName) =>
        set(state => {
          const c = state.containers[containerId]
          if (!c) return state
          return {
            containers: {
              ...state.containers,
              [containerId]: {
                ...c,
                name: newName,
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      ensureLocalJam: () => {
        const state = get()
        if (state.containers[LOCAL_JAM_CONTAINER_ID]) {
          return LOCAL_JAM_CONTAINER_ID
        }
        const firstPatternId = genId('p')
        const firstPattern = createProject(firstPatternId, 'Pattern 1')
        const jam = createEmptyContainer(
          LOCAL_JAM_CONTAINER_ID,
          'Public jam · local only',
          firstPatternId,
        )
        set({
          projects: { ...state.projects, [firstPatternId]: firstPattern },
          containers: {
            ...state.containers,
            [LOCAL_JAM_CONTAINER_ID]: jam,
          },
        })
        return LOCAL_JAM_CONTAINER_ID
      },

      saveDefaultProject: name => {
        const state = get()
        const sourceId = DEFAULT_CONTAINER_ID
        const source = state.containers[sourceId]
        if (!source) return null
        // Only promote when the current focus is actually the default
        // scratch pad — calling from a non-default container is almost
        // certainly a wiring mistake.
        if (state.currentContainerId !== sourceId) return null

        const newId = genId('proj')
        const now = new Date().toISOString()
        const trimmed = name.trim() || 'Untitled project'
        const promoted: ProjectContainer = {
          ...source,
          id: newId,
          name: trimmed,
          createdAt: source.createdAt ?? now,
          updatedAt: now,
        }

        // Re-seed a pristine default so the user has a clean scratch
        // pad for their next session. Pick a fresh pattern id so the
        // pool doesn't collide with the promoted container's
        // patterns (which now live under the new UUID).
        const freshPatternId = genId('p')
        const freshPattern = createProject(freshPatternId, 'Pattern 1')
        const freshDefault = createEmptyContainer(
          DEFAULT_CONTAINER_ID,
          'Untitled project',
          freshPatternId,
        )

        const nextContainers = {
          ...state.containers,
          [newId]: promoted,
          [DEFAULT_CONTAINER_ID]: freshDefault,
        }
        const nextProjects = {
          ...state.projects,
          [freshPatternId]: freshPattern,
        }

        set({
          containers: nextContainers,
          projects: nextProjects,
          currentContainerId: newId,
          // Keep the pattern focus on whatever the user had open in
          // the promoted container — that's the work they're saving.
          currentProjectId: state.currentProjectId,
        })
        return newId
      },

      deleteContainer: containerId =>
        set(state => {
          const remaining = { ...state.containers }
          delete remaining[containerId]
          // Clear the sync record too. Without this, the push loop
          // kept the ghost entry alive: PUT → 404 → re-POST → a new
          // server row appears under a fresh uuid — which is how the
          // "Untitled project" list kept climbing back to the cap
          // even after the user bulk-deleted everything.
          const nextSyncState = { ...state.containerSyncState }
          delete nextSyncState[containerId]
          if (Object.keys(remaining).length === 0) {
            const firstPatternId = Object.keys(state.projects)[0] ?? ''
            return {
              containers: defaultContainers(firstPatternId),
              currentContainerId: DEFAULT_CONTAINER_ID,
              containerSyncState: nextSyncState,
            }
          }
          const nextCurrent =
            state.currentContainerId === containerId
              ? Object.keys(remaining)[0]
              : state.currentContainerId
          return {
            containers: remaining,
            currentContainerId: nextCurrent,
            containerSyncState: nextSyncState,
          }
        }),

      selectBank: bankId =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          const bank = c.banks[bankId]
          if (!bank) return state
          // Auto-seed a pattern if the bank is empty so the user has
          // something to edit immediately after switching.
          let projects = state.projects
          let patternIds = bank.patternIds
          let activePatternId = bank.activePatternId
          if (patternIds.length === 0) {
            const patternId = genId('p')
            projects = {
              ...projects,
              [patternId]: createProject(patternId, 'Pattern 1'),
            }
            patternIds = [patternId]
            activePatternId = patternId
          } else if (
            !activePatternId ||
            !patternIds.includes(activePatternId)
          ) {
            activePatternId = patternIds[0]
          }
          return {
            projects,
            currentProjectId: activePatternId,
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                activeBankId: bankId,
                banks: {
                  ...c.banks,
                  [bankId]: { ...bank, activePatternId, patternIds },
                },
                updatedAt: new Date().toISOString(),
              },
            },
            // Don't reset isPlaying / currentStep — the scheduler reads
            // the active pattern's steps/bpm via a store selector on
            // each tick, so switching banks mid-play transparently
            // hot-swaps what's firing without interrupting the clock.
            focusedPad: 0,
            trackerFocus: null,
          }
        }),

      renameBank: (bankId, label) =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          const bank = c.banks[bankId]
          if (!bank) return state
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                banks: { ...c.banks, [bankId]: { ...bank, label } },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setBankKit: (bankId, kitId) =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          const bank = c.banks[bankId]
          if (!bank) return state
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                banks: { ...c.banks, [bankId]: { ...bank, kitId } },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setActivePatternInBank: (bankId, patternId) =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          const bank = c.banks[bankId]
          if (!bank) return state
          if (!bank.patternIds.includes(patternId)) return state
          return {
            currentProjectId:
              bankId === c.activeBankId ? patternId : state.currentProjectId,
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                banks: {
                  ...c.banks,
                  [bankId]: { ...bank, activePatternId: patternId },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      addPatternToBank: (bankId, name) => {
        const state = get()
        const c = state.containers[state.currentContainerId]
        if (!c) return ''
        const targetBankId: BankId = bankId ?? c.activeBankId
        const bank = c.banks[targetBankId]
        if (!bank) return ''
        // 16-pattern cap per bank. Matches SP-404 Pattern Select
        // (pads 1-16 → patterns 1-16) so the pad-to-pattern mapping
        // in Pattern mode always fits. Callers that hit the cap get
        // the existing "no-op → empty id" sentinel; UI greys the
        // `+` button so the user doesn't get surprised.
        if (bank.patternIds.length >= 16) return ''
        // Name auto-increments across the *bank's* existing patterns
        // to match the classic "Pattern 1..N" convention.
        const existingNumbers = bank.patternIds
          .map(pid => state.projects[pid]?.name)
          .filter((n): n is string => !!n)
          .map(n => n.match(/^Pattern\s+(\d+)$/i)?.[1])
          .filter((n): n is string => !!n)
          .map(Number)
        const nextN = existingNumbers.length
          ? Math.max(...existingNumbers) + 1
          : 1
        const patternId = genId('p')
        const pattern = createProject(patternId, name ?? `Pattern ${nextN}`)
        set({
          projects: { ...state.projects, [patternId]: pattern },
          currentProjectId:
            targetBankId === c.activeBankId
              ? patternId
              : state.currentProjectId,
          containers: {
            ...state.containers,
            [state.currentContainerId]: {
              ...c,
              banks: {
                ...c.banks,
                [targetBankId]: {
                  ...bank,
                  patternIds: [...bank.patternIds, patternId],
                  activePatternId: patternId,
                },
              },
              updatedAt: new Date().toISOString(),
            },
          },
        })
        return patternId
      },

      setPatternSwing: amount =>
        set(state =>
          updateCurrent(state, p => {
            const clamped = Math.max(0, Math.min(0.75, amount))
            // Store `undefined` for a true-zero swing so legacy
            // (never-set) patterns and explicitly-zeroed patterns
            // serialise identically.
            const next = clamped > 0 ? clamped : undefined
            if (p.swing === next) return p
            return { ...p, swing: next }
          }),
        ),

      setPatternLoopRange: (inStep, outStep) =>
        set(state =>
          updateCurrent(state, p => {
            const maxStep = p.stepCount - 1
            // Normalise: either side null → both cleared (either-
            // endpoint looping is ambiguous — clear is safer than
            // half-open).
            if (inStep === null || outStep === null) {
              if (p.loopInStep === undefined && p.loopOutStep === undefined) {
                return p
              }
              return { ...p, loopInStep: undefined, loopOutStep: undefined }
            }
            const lo = Math.max(0, Math.min(maxStep, Math.floor(inStep)))
            const hi = Math.max(lo, Math.min(maxStep, Math.floor(outStep)))
            if (p.loopInStep === lo && p.loopOutStep === hi) return p
            return { ...p, loopInStep: lo, loopOutStep: hi }
          }),
        ),

      duplicatePattern: (sourcePatternId, targetBankId, newName) => {
        const state = get()
        const c = state.containers[state.currentContainerId]
        if (!c) return ''
        const source = state.projects[sourcePatternId]
        if (!source) return ''
        // Locate the source's home bank unless the caller overrode.
        // Scanning is fine — at most 4 banks × N patterns.
        let sourceBankId: BankId | null = null
        for (const bankId of ['A', 'B', 'C', 'D'] as const) {
          if (c.banks[bankId]?.patternIds.includes(sourcePatternId)) {
            sourceBankId = bankId
            break
          }
        }
        const resolvedTarget: BankId =
          targetBankId ?? sourceBankId ?? c.activeBankId
        const targetBank = c.banks[resolvedTarget]
        if (!targetBank) return ''

        const patternId = genId('p')
        // Deep-clone steps cell-by-cell so nested objects (fx, roll,
        // envelope) stay independent. Spread-copy alone leaves
        // shared references the user can mutate later.
        const clonedSteps: typeof source.steps = {}
        for (const [padIdx, row] of Object.entries(source.steps)) {
          clonedSteps[Number(padIdx)] = row.map(cell => ({
            ...cell,
            fx: cell.fx ? { ...cell.fx } : undefined,
            envelope: cell.envelope ? { ...cell.envelope } : undefined,
            roll: cell.roll ? { ...cell.roll } : undefined,
          }))
        }
        const clonedMidiPadSteps: typeof source.midiPadSteps = {}
        for (const [padId, row] of Object.entries(source.midiPadSteps)) {
          clonedMidiPadSteps[padId] = row.map(cell => ({
            ...cell,
            fx: cell.fx ? { ...cell.fx } : undefined,
            envelope: cell.envelope ? { ...cell.envelope } : undefined,
            roll: cell.roll ? { ...cell.roll } : undefined,
          }))
        }
        const defaultName = `${source.name} copy`
        const cloned: Project = {
          ...source,
          id: patternId,
          name: newName ?? defaultName,
          steps: clonedSteps,
          midiPadSteps: clonedMidiPadSteps,
        }

        set({
          projects: { ...state.projects, [patternId]: cloned },
          currentProjectId:
            resolvedTarget === c.activeBankId
              ? patternId
              : state.currentProjectId,
          containers: {
            ...state.containers,
            [state.currentContainerId]: {
              ...c,
              banks: {
                ...c.banks,
                [resolvedTarget]: {
                  ...targetBank,
                  patternIds: [...targetBank.patternIds, patternId],
                  activePatternId: patternId,
                },
              },
              updatedAt: new Date().toISOString(),
            },
          },
        })
        return patternId
      },

      removePatternFromBank: (bankId, patternId) =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          const bank = c.banks[bankId]
          if (!bank) return state
          if (!bank.patternIds.includes(patternId)) return state
          const remainingIds = bank.patternIds.filter(p => p !== patternId)
          const nextActivePatternId =
            bank.activePatternId === patternId
              ? (remainingIds[0] ?? '')
              : bank.activePatternId
          // Also delete the underlying flat Pattern entry — patterns
          // are bank-internal and not referenced from anywhere else.
          const nextProjects = { ...state.projects }
          delete nextProjects[patternId]
          return {
            projects: nextProjects,
            currentProjectId:
              state.currentProjectId === patternId
                ? nextActivePatternId
                : state.currentProjectId,
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                banks: {
                  ...c.banks,
                  [bankId]: {
                    ...bank,
                    patternIds: remainingIds,
                    activePatternId: nextActivePatternId,
                  },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      initProjectMidiOverride: () =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          // Lift whatever's in the global midiStore at this moment.
          const ms = useMidiStore.getState()
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                midiConfig: {
                  routes: ms.routes.map(r => ({
                    inputId: r.inputId,
                    outputId: r.outputId,
                  })),
                  outputLatencyMs: { ...ms.outputLatencyMs },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      clearProjectMidiOverride: () =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c || !c.midiConfig) return state
          const { midiConfig: _dropped, ...rest } = c
          void _dropped
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...rest,
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setProjectMidiRoutes: routes =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c || !c.midiConfig) return state
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                midiConfig: { ...c.midiConfig, routes },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setProjectOutputLatency: (outputId, ms) =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c || !c.midiConfig) return state
          const current = c.midiConfig.outputLatencyMs ?? {}
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                midiConfig: {
                  ...c.midiConfig,
                  outputLatencyMs: { ...current, [outputId]: ms },
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      addProjectSample: sampleId =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          if (c.slicerConfig.sampleIds.includes(sampleId)) return state
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                slicerConfig: {
                  ...c.slicerConfig,
                  sampleIds: [...c.slicerConfig.sampleIds, sampleId],
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      removeProjectSample: sampleId =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                slicerConfig: {
                  ...c.slicerConfig,
                  sampleIds: c.slicerConfig.sampleIds.filter(
                    id => id !== sampleId,
                  ),
                  activeSampleId:
                    c.slicerConfig.activeSampleId === sampleId
                      ? undefined
                      : c.slicerConfig.activeSampleId,
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),

      setActiveProjectSample: sampleId =>
        set(state => {
          const c = state.containers[state.currentContainerId]
          if (!c) return state
          return {
            containers: {
              ...state.containers,
              [state.currentContainerId]: {
                ...c,
                slicerConfig: {
                  ...c.slicerConfig,
                  activeSampleId: sampleId,
                },
                updatedAt: new Date().toISOString(),
              },
            },
          }
        }),
    }),
    {
      name: 'busswerks-sequencer',
      partialize: state => ({
        projects: state.projects,
        currentProjectId: state.currentProjectId,
        containers: state.containers,
        currentContainerId: state.currentContainerId,
        containerSyncState: state.containerSyncState,
        layoutMode: state.layoutMode,
        fxLevels: state.fxLevels,
        perfFilterMode: state.perfFilterMode,
        songMode: state.songMode,
        song: state.song,
        padMuted: state.padMuted,
      }),
      storage: createJSONStorage(() =>
        typeof window === 'undefined' ? memoryStorage() : window.localStorage,
      ),
      /*
       * Versions:
       *  0 — undefined (legacy, no version field): single `project` field,
       *      `tr808` kit id, no stepCount, steps were `boolean[]`.
       *  1 — single `project` field, `classic` kit id, `boolean[]` steps.
       *  2 — projects map, stepCount per project, `boolean[]` steps,
       *      layoutMode, four kits (classic/warehouse/crate/prism).
       *  3 — steps are StepCell[] (adds velocity / pitch / fx / slice),
       *      layouts gain 'tracker' mode.
       *  4 — projects gain `midiPads` + `midiPadSteps`; StepCell gains
       *      `length`; root gains `fxLevels`, `song`, `songMode`;
       *      StepCount widens to include 128.
       *  5 — Project gains `customSamples: Record<padIndex,
       *      CustomSampleRef>` for Plan 2b user-uploaded samples.
       *  6 — Project gains `padOutputs: Record<padIndex,
       *      PadMidiRouting>` + MidiPadDef gains `outputId?` for
       *      per-pad MIDI routing. Both optional — unset entries
       *      broadcast like before.
       *  7 — Project gains `padDefs: Record<padIndex, PadContent>`
       *      for arbitrary per-pad content (synth voice / sample /
       *      external MIDI). Legacy `customSamples` entries are
       *      lifted into `padDefs[i] = { kind: 'sample', ... }`;
       *      the `customSamples` field is retained for one release
       *      cycle for read-compat but never written to.
       *  8 — Sequencer terminology: "Pattern" renamed to "Project"
       *      across types + UI. Persisted key rename:
       *      `patterns` → `projects`, `currentPatternId` → `currentProjectId`,
       *      SongEntry.`patternId` → `projectId`. Data is unchanged.
       *  9 — LayoutMode drops `'bars'` (redundant vs `'grid'` at
       *      stepCount=64). Persisted `layoutMode === 'bars'` lands
       *      on `'grid'` via the normalisation below. Label strings
       *      in LAYOUT_MODES also rename `tracks` → "2×8" and
       *      `quads` → "4×4" but the internal ids don't change, so
       *      no data migration is needed for those.
       * 10 — Adds the Project → Bank → Pattern container layer.
       *      The existing flat `projects` map is preserved in place
       *      (each entry is now semantically a Pattern). A single
       *      container is seeded with `activeBankId: 'A'` holding
       *      every existing pattern id in `banks.A.patternIds`, and
       *      the pad-layout fields of the most-recently-updated
       *      pattern are lifted into `banks.A` (kitId, padDefs,
       *      padOutputs, customSamples, midiPads, keysMode). Old
       *      flat `song` entries are left in place; the new
       *      container gets an empty arrangement that users can
       *      rebuild against the bank/pattern pair format.
       * 12 — Adds per-container `buses` (mixer groups with volume +
       *      pan). Every existing container gets a single master
       *      bus seeded; banks keep `padBus` undefined so all pads
       *      route through master by default. No audible change —
       *      master volume defaults to 1, master pan to 0.
       */
      version: 13,
      migrate: (persisted: unknown, fromVersion) => {
        if (!persisted || typeof persisted !== 'object') {
          return {
            projects: defaultProjects(),
            currentProjectId: DEFAULT_PROJECT_ID,
            layoutMode: 'grid',
            fxLevels: defaultFxLevels(),
            song: [],
            songMode: false,
          }
        }
        const raw = persisted as Record<string, unknown>
        // v8: if persisted shape still uses the pre-rename keys,
        // shim them into the new names so the shared migration below
        // can run unchanged.
        const state: Record<string, unknown> =
          fromVersion < 8
            ? {
                ...raw,
                projects: raw.projects ?? raw.patterns,
                currentProjectId: raw.currentProjectId ?? raw.currentPatternId,
                song: Array.isArray(raw.song)
                  ? (raw.song as unknown[]).map(entry => {
                      if (!entry || typeof entry !== 'object') return entry
                      const e = entry as Record<string, unknown>
                      return {
                        ...e,
                        projectId: e.projectId ?? e.patternId,
                      }
                    })
                  : raw.song,
              }
            : raw
        const validKits: KitId[] = ['classic', 'warehouse', 'crate', 'prism']
        const validLayouts: LayoutMode[] = [
          'tracks',
          'grid',
          'quads',
          'tracker',
        ]
        // v9: collapse removed `'bars'` onto `'grid'` before the
        // validLayouts gate rejects it. Any other unknown layout
        // still falls through to the default.
        if (state.layoutMode === 'bars') {
          state.layoutMode = 'grid'
        }

        // v0/v1 single-project shape → v2 projects-map shape first.
        let projects: Record<string, Project>
        let currentProjectId: string

        if (fromVersion < 2 && state.project) {
          const legacy = state.project as Partial<Project> & {
            steps?: unknown
          }
          const stepCount: StepCount = 16
          const kitId: KitId = validKits.includes(legacy.kitId as KitId)
            ? (legacy.kitId as KitId)
            : 'classic'
          const id = legacy.id ?? DEFAULT_PROJECT_ID
          const rawSteps = (legacy.steps ?? {}) as Record<string, unknown>
          const upgradedSteps: StepGrid = {}
          for (let i = 0; i < PADS_PER_PROJECT; i++) {
            upgradedSteps[i] = upgradeRow(rawSteps[i])
          }
          const wrapped: Project = {
            id,
            name: legacy.name ?? 'Project 1',
            bpm: typeof legacy.bpm === 'number' ? legacy.bpm : 120,
            kitId,
            stepCount,
            midiEnabled: legacy.midiEnabled ?? true,
            midiInEnabled: legacy.midiInEnabled ?? true,
            steps: resizeStepGrid(upgradedSteps, stepCount),
            midiPads: [],
            midiPadSteps: {},
          }
          projects = { [id]: wrapped }
          currentProjectId = id
        } else {
          // v2+ — projects map present. Upgrade each project's steps to
          // StepCell[] and sanity-check fields. Also default the new
          // v4 custom-pad fields.
          const pMap = (state.projects ?? {}) as Record<string, unknown>
          const sanitized: Record<string, Project> = {}
          for (const [id, raw] of Object.entries(pMap)) {
            const p = raw as Partial<Project> & { steps?: unknown }
            const kitId = validKits.includes(p.kitId as KitId)
              ? (p.kitId as KitId)
              : 'classic'
            const stepCount = (
              STEP_COUNTS.includes(p.stepCount as StepCount) ? p.stepCount : 16
            ) as StepCount
            const rawSteps = (p.steps ?? {}) as Record<string, unknown>
            const upgradedSteps: StepGrid = {}
            for (let i = 0; i < PADS_PER_PROJECT; i++) {
              upgradedSteps[i] = upgradeRow(rawSteps[i])
            }
            const midiPads: MidiPadDef[] = Array.isArray(p.midiPads)
              ? p.midiPads
                  .filter(
                    (mp): mp is MidiPadDef =>
                      !!mp &&
                      typeof mp === 'object' &&
                      typeof (mp as MidiPadDef).id === 'string',
                  )
                  .map(mp => {
                    const base: MidiPadDef = {
                      id: mp.id,
                      label: mp.label ?? 'MIDI',
                      note: clamp127(mp.note ?? 60),
                      channel: clampChannel(mp.channel ?? 1),
                    }
                    // v6: preserve per-pad output if present
                    if (typeof mp.outputId === 'string') {
                      base.outputId = mp.outputId
                    }
                    return base
                  })
              : []
            const rawMidiSteps = (p.midiPadSteps ?? {}) as Record<
              string,
              unknown
            >
            const midiPadSteps: Record<string, StepCell[]> = {}
            for (const mp of midiPads) {
              midiPadSteps[mp.id] = upgradeRow(rawMidiSteps[mp.id])
            }
            // v5: sanitize customSamples shape. Entries missing
            // sampleId or s3Key get dropped — they're unusable
            // without the S3 reference.
            const rawSamples = (p.customSamples ?? {}) as Record<
              string,
              unknown
            >
            const customSamples: Record<number, CustomSampleRef> = {}
            for (const [padIdx, entry] of Object.entries(rawSamples)) {
              if (!entry || typeof entry !== 'object') continue
              const e = entry as Record<string, unknown>
              if (
                typeof e.sampleId !== 'string' ||
                typeof e.s3Key !== 'string'
              ) {
                continue
              }
              const idx = Number(padIdx)
              if (
                !Number.isInteger(idx) ||
                idx < 0 ||
                idx >= PADS_PER_PROJECT
              ) {
                continue
              }
              customSamples[idx] = {
                sampleId: e.sampleId,
                s3Key: e.s3Key,
                name: typeof e.name === 'string' ? e.name : undefined,
              }
            }

            // v6: sanitize padOutputs shape. Values must be a
            // string; "off" is allowed as-is, other strings are
            // treated as candidate output ids (runtime resolution
            // verifies they still exist).
            const rawPadOutputs = (p.padOutputs ?? {}) as Record<
              string,
              unknown
            >
            const padOutputs: Record<number, PadMidiRouting> = {}
            for (const [padIdx, entry] of Object.entries(rawPadOutputs)) {
              if (typeof entry !== 'string' || entry.length === 0) continue
              const idx = Number(padIdx)
              if (
                !Number.isInteger(idx) ||
                idx < 0 ||
                idx >= PADS_PER_PROJECT
              ) {
                continue
              }
              padOutputs[idx] = entry as PadMidiRouting
            }

            // v7: sanitize padDefs + promote legacy customSamples
            // into the new shape. Any entry already present in
            // padDefs wins over the legacy customSamples lift —
            // keeps round-trips stable for projects written after
            // v7 shipped.
            const rawPadDefs = (p.padDefs ?? {}) as Record<string, unknown>
            const padDefs: Record<number, PadContent> = {}
            const validVoices: VoiceType[] = [
              'kick',
              'snare',
              'hatClosed',
              'hatOpen',
              'clap',
              'tom',
              'ride',
              'cowbell',
            ]
            for (const [padIdx, entry] of Object.entries(rawPadDefs)) {
              if (!entry || typeof entry !== 'object') continue
              const idx = Number(padIdx)
              if (
                !Number.isInteger(idx) ||
                idx < 0 ||
                idx >= PADS_PER_PROJECT
              ) {
                continue
              }
              const e = entry as Record<string, unknown>
              // Fall back to the chromatic-by-pad default (48+idx,
              // i.e. C3 at pad 0) when the stored entry is missing
              // or non-numeric — matches what PadEditor now uses for
              // fresh pads, so corrupt rows don't spawn a stray 60.
              const midiNote =
                typeof e.midiNote === 'number'
                  ? clamp127(e.midiNote)
                  : clamp127(48 + idx)
              if (
                e.kind === 'synth' &&
                typeof e.voice === 'string' &&
                validVoices.includes(e.voice as VoiceType)
              ) {
                padDefs[idx] = {
                  kind: 'synth',
                  voice: e.voice as VoiceType,
                  midiNote,
                  ...(typeof e.label === 'string' && { label: e.label }),
                }
              } else if (
                e.kind === 'sample' &&
                typeof e.sampleId === 'string' &&
                typeof e.s3Key === 'string'
              ) {
                padDefs[idx] = {
                  kind: 'sample',
                  sampleId: e.sampleId,
                  s3Key: e.s3Key,
                  midiNote,
                  ...(typeof e.name === 'string' && { name: e.name }),
                }
              } else if (e.kind === 'midi' && typeof e.channel === 'number') {
                padDefs[idx] = {
                  kind: 'midi',
                  label: typeof e.label === 'string' ? e.label : 'MIDI',
                  midiNote,
                  channel: clampChannel(e.channel),
                }
              }
            }
            // Promote legacy customSamples into padDefs for any
            // slot that doesn't already have an explicit padDef.
            // Use the chromatic-by-pad default (48+idx) so the
            // lifted sample shares the same MIDI-in matching as a
            // freshly-added sample would — avoids a silent
            // divergence where pre-v7 samples couldn't be triggered
            // from an external controller even though v7+ ones could.
            for (const [idxStr, ref] of Object.entries(customSamples)) {
              const idx = Number(idxStr)
              if (padDefs[idx] !== undefined) continue
              padDefs[idx] = {
                kind: 'sample',
                sampleId: ref.sampleId,
                s3Key: ref.s3Key,
                midiNote: clamp127(48 + idx),
                ...(ref.name && { name: ref.name }),
              }
            }

            // Keys mode — v7 forward-compat: honour if the field
            // exists, clamp numeric subfields, ignore unknown
            // scales.
            const validScales: ScaleId[] = [
              'chromatic',
              'major',
              'minor',
              'pentatonicMajor',
              'pentatonicMinor',
              'dorian',
              'mixolydian',
              'blues',
            ]
            let keysMode: KeysModeConfig | undefined
            if (p.keysMode && typeof p.keysMode === 'object') {
              const k = p.keysMode as Partial<KeysModeConfig>
              const scale =
                typeof k.scale === 'string' && validScales.includes(k.scale)
                  ? k.scale
                  : 'chromatic'
              keysMode = {
                enabled: k.enabled === true,
                rootNote: clamp127(
                  typeof k.rootNote === 'number' ? k.rootNote : 60,
                ),
                scale,
                channel: clampChannel(
                  typeof k.channel === 'number' ? k.channel : 1,
                ),
                ...(typeof k.output === 'string' && { output: k.output }),
              }
            }

            sanitized[id] = {
              id,
              name: p.name ?? 'Project',
              bpm: typeof p.bpm === 'number' ? p.bpm : 120,
              kitId,
              stepCount,
              midiEnabled: p.midiEnabled ?? true,
              midiInEnabled: p.midiInEnabled ?? true,
              steps: resizeStepGrid(upgradedSteps, stepCount),
              midiPads,
              midiPadSteps: resizeMidiPadSteps(midiPadSteps, stepCount),
              ...(Object.keys(customSamples).length > 0 && { customSamples }),
              ...(Object.keys(padOutputs).length > 0 && { padOutputs }),
              ...(Object.keys(padDefs).length > 0 && { padDefs }),
              ...(keysMode && { keysMode }),
            }
          }
          projects = sanitized
          const ids = Object.keys(projects)
          if (ids.length === 0) {
            projects = defaultProjects()
            currentProjectId = DEFAULT_PROJECT_ID
          } else {
            currentProjectId =
              typeof state.currentProjectId === 'string' &&
              projects[state.currentProjectId]
                ? state.currentProjectId
                : ids[0]
          }
        }

        const layoutMode = validLayouts.includes(state.layoutMode as LayoutMode)
          ? (state.layoutMode as LayoutMode)
          : 'grid'

        // fxLevels / song / songMode only exist in v4+; fall back to
        // defaults if the persisted shape predates them.
        const rawLevels = (state.fxLevels ?? {}) as Record<string, unknown>
        const fxLevels = defaultFxLevels()
        for (const id of FX_IDS) {
          const v = rawLevels[id]
          if (typeof v === 'number' && v >= 0 && v <= 1) {
            fxLevels[id] = v
          }
        }
        const song = Array.isArray(state.song)
          ? (state.song as unknown[])
              .filter(
                (e): e is SongEntry =>
                  !!e &&
                  typeof e === 'object' &&
                  typeof (e as SongEntry).id === 'string' &&
                  typeof (e as SongEntry).projectId === 'string' &&
                  !!projects[(e as SongEntry).projectId],
              )
              .map(e => ({
                id: e.id,
                projectId: e.projectId,
                repeats: Math.max(1, Math.round(e.repeats ?? 1)),
              }))
          : []
        const songMode = state.songMode === true

        // ── v9→v10: seed the container layer ──
        // If the persisted blob already has containers (i.e. this
        // migration has run before), keep them; otherwise wrap every
        // existing pattern id into the default container's bank A and
        // lift pad-layout fields from the most-recently-updated
        // pattern so the user's current pad setup is preserved in the
        // bank.
        const existingContainersRaw = state.containers
        let containers: Record<string, ProjectContainer>
        let currentContainerId: string
        if (
          existingContainersRaw &&
          typeof existingContainersRaw === 'object' &&
          !Array.isArray(existingContainersRaw) &&
          Object.keys(existingContainersRaw).length > 0
        ) {
          containers = existingContainersRaw as Record<string, ProjectContainer>
          currentContainerId =
            typeof state.currentContainerId === 'string' &&
            state.currentContainerId in containers
              ? (state.currentContainerId as string)
              : Object.keys(containers)[0]
        } else {
          const patternIds = Object.keys(projects)
          // Pick the current pattern as the representative for bank A's
          // pad layout so the editor opens looking exactly the same.
          const repPattern =
            projects[currentProjectId] ?? projects[patternIds[0]]
          const bankASeed: Partial<Omit<ProjectContainerBank, 'id' | 'label'>> =
            repPattern
              ? {
                  kitId: repPattern.kitId,
                  padDefs: repPattern.padDefs,
                  padOutputs: repPattern.padOutputs,
                  customSamples: repPattern.customSamples,
                  midiPads: repPattern.midiPads ?? [],
                  midiEnabled: repPattern.midiEnabled,
                  keysMode: repPattern.keysMode,
                  activePatternId: currentProjectId,
                  patternIds,
                }
              : { patternIds }
          containers = {
            [DEFAULT_CONTAINER_ID]: createEmptyContainer(
              DEFAULT_CONTAINER_ID,
              'Untitled project',
              currentProjectId,
              bankASeed,
            ),
          }
          currentContainerId = DEFAULT_CONTAINER_ID
        }

        // v10→v11: seed each container's `scenes` from the legacy
        // flat `song` list when it's missing. Each old entry becomes
        // a single-column scene — the pattern drops into whichever
        // bank owns it, others stay `null` (silent). `lengthSteps`
        // defaults to repeats × 16 so old loops play for roughly
        // the same duration as before under the new scheduler.
        const migratedContainers: Record<string, ProjectContainer> = {}
        for (const [cid, rawC] of Object.entries(containers)) {
          const c = rawC as ProjectContainer & {
            scenes?: SongScene[] | undefined
          }
          if (Array.isArray(c.scenes)) {
            migratedContainers[cid] = c as ProjectContainer
            continue
          }
          const scenes: SongScene[] = Array.isArray(c.song)
            ? (c.song as ContainerSongEntry[])
                .filter(e => !!e && typeof e.patternId === 'string')
                .map(e => {
                  const bankId = (
                    e.bankId && ['A', 'B', 'C', 'D'].includes(e.bankId)
                      ? e.bankId
                      : 'A'
                  ) as BankId
                  const cells: Record<BankId, string | null> = {
                    A: null,
                    B: null,
                    C: null,
                    D: null,
                  }
                  cells[bankId] = e.patternId
                  return {
                    id: genId('scene'),
                    name: '',
                    lengthSteps: Math.max(1, Math.round(e.repeats ?? 1)) * 16,
                    cells,
                  }
                })
            : []
          migratedContainers[cid] = { ...c, scenes }
        }

        // v12: seed `buses` on every container that doesn't already
        // have one. Master bus is mandatory — users and the engine
        // both expect it to exist so pads without a `padBus` entry
        // have somewhere to land. Idempotent because we only add
        // when the field is absent.
        //
        // v13: ensure Master has an FFT Analyzer seeded as its first
        // effect. Prior versions tried to seed via a post-mount
        // `useEffect` in SequencerPage, but the race against the
        // container-sync pull dropped the seed before the Inspector
        // could see it. Migrating persisted state here + creating
        // every new container with the analyzer already attached
        // guarantees the Inspector's Master FX chain always shows
        // it. Uses the MASTER_FFT_ANALYZER_EFFECT_ID sentinel so
        // re-running the migration doesn't stack duplicate rows.
        for (const cid of Object.keys(migratedContainers)) {
          const c = migratedContainers[cid]
          if (!c) continue
          const buses = c.buses ?? [
            { id: MASTER_BUS_ID, name: 'Master', volume: 1, pan: 0 },
          ]
          const nextBuses = buses.map(b => {
            if (b.id !== MASTER_BUS_ID) return b
            const effects = b.effects ?? []
            const hasAnalyzer = effects.some(e => e.pluginId === 'fft-analyzer')
            if (hasAnalyzer) return b
            return {
              ...b,
              effects: [
                ...effects,
                {
                  id: MASTER_FFT_ANALYZER_EFFECT_ID,
                  kind: 'plugin' as const,
                  pluginId: 'fft-analyzer',
                  enabled: true,
                },
              ],
            }
          })
          migratedContainers[cid] = { ...c, buses: nextBuses }
        }

        return {
          projects,
          currentProjectId,
          containers: migratedContainers,
          currentContainerId,
          layoutMode,
          fxLevels,
          song,
          songMode,
        }
      },
      /**
       * Post-rehydrate self-heal. Runs on every load — including the
       * common case where `fromVersion === version` (and so the
       * `migrate` callback above is skipped entirely). This guarantees
       * that every container in the rehydrated state has a master bus
       * carrying the FFT analyzer regardless of how its persisted
       * state got there (pre-v13, post-v13, a manual edit that
       * stripped the row, a corrupted rehydrate, etc).
       *
       * `ensureMasterFftEffect` is idempotent, so calling it on an
       * already-healed container is a no-op that returns the same
       * reference. Only containers that actually needed a patch get
       * new object identity + a write through `setState`.
       */
      onRehydrateStorage: () => state => {
        if (!state || !state.containers) return
        let mutated = false
        const next: Record<string, ProjectContainer> = {}
        for (const [id, container] of Object.entries(state.containers)) {
          if (!container) continue
          const healed = ensureMasterFftEffect(container)
          if (healed !== container) mutated = true
          next[id] = healed
        }
        if (mutated) {
          useSequencerStore.setState({ containers: next })
        }
      },
    },
  ),
)

function memoryStorage(): Storage {
  const store = new Map<string, string>()
  return {
    get length() {
      return store.size
    },
    clear: () => store.clear(),
    getItem: key => store.get(key) ?? null,
    key: index => Array.from(store.keys())[index] ?? null,
    removeItem: key => {
      store.delete(key)
    },
    setItem: (key, value) => {
      store.set(key, value)
    },
  }
}
